import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Edit3, Plus, Info, Trash2, Target, Flame, Award, ShieldCheck, ChevronUp, ChevronDown, BrainCircuit } from 'lucide-react';
import { Table, Button, Input, Checkbox, Badge, Tooltip, Space, Tag, Modal, Select } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../../contexts/ThemeContext';
import { useGoalsSettings } from '../../../contexts/GoalsSettingsContext';
import toast from 'react-hot-toast';

const GoalsTable = ({
  goals,
  onApprove,
  onReject,
  onDelete,
  onEdit,
  onSetAchievedPercent,
  onCalculateScore,
  canAddGoal = true,
  onAddGoal,
  isGoalsCycleEditable,
  inlineNewGoal,
  setInlineNewGoal,
  editingGoalId,
  setEditingGoalId,
  onSaveNewGoal,
  onSaveEditGoal,
  onCancelEdit,
  minGoals = 0,
  maxGoals = 0,
  criticalRequired = 0,
  minWeightage = 10,
  maxWeightage = 100,
  isManager = false,
  isViewingTeamMember = false,
  isSubmissionWindowActive = false,
  isReviewWindowActive = false,
  isEvaluationWindowActive = false,
  currentUserId = null,
  cycleId = null,
  targetUserId = null,
  currentCycle = null,
  onSubmitGoals = null,
  scoreRanges = [],
  isOrgAdmin = false,
  isAutonomousMode = false,
  onToggleCritical = null,
  aiGoalEnabled = false,
  onToggleAiGoal = null,
  projectsList = [],
  cycleScoringMode = 'legacy',
  softSkillsData = [],
  reviewCompletedAt = null,
  onMarkReviewComplete = null,
  isMarkingReviewComplete = false,
  onSelfEvaluate = null,
  onVerifyGoal = null,
}) => {
  const isUnifiedMode = cycleScoringMode === 'unified';
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const { settings: goalsSettings } = useGoalsSettings();
  const badgeRanges = goalsSettings?.badgeRanges || [];
  // Star flow: manager assigns weightage points; completion is binary (done/not done).
  // ManagerView doesn't forward cycleScoringMode, so also derive from currentCycle.
  const isStarMode = cycleScoringMode === 'star5' || currentCycle?.scoringMode === 'star5';
  const orgFlowMode = goalsSettings?.goalsMode || 'classic';
  const isChecklistMode = orgFlowMode === 'checklist';
  const isScoreOnlyMode = orgFlowMode === 'score_only';
  // Windowless modes: adding/scoring is always open — treat every window as
  // active so all downstream gates (add row, save, footer, score input) unlock.
  if (isChecklistMode || isScoreOnlyMode) {
    isSubmissionWindowActive = true;
    isReviewWindowActive = true;
    isEvaluationWindowActive = true;
    isGoalsCycleEditable = true;
  }

  // Normalize weight to multiple of 10 within min/max (star mode: any whole number)
  const normalizeWeight = useCallback((val) => {
    const n = parseInt(val, 10);
    if (isNaN(n)) return minWeightage;
    const rounded = isStarMode ? n : Math.round(n / 10) * 10;
    return Math.max(minWeightage, Math.min(maxWeightage, rounded));
  }, [minWeightage, maxWeightage, isStarMode]);

  // Local state for input values to prevent focus loss
  // Initialize from inlineNewGoal only when it's first set or cleared
  const [localInputState, setLocalInputState] = useState(() => ({
    name: '',
    metric: '= 100%',
    weight: minWeightage,
    critical: false,
    projectId: '',
  }));

  // Local state for editing goal to prevent focus loss
  const [editingGoalState, setEditingGoalState] = useState(null);

  // Local state for score inputs (per goal)
  const [scoreInputs, setScoreInputs] = useState({});

  // Loading states for approve/reject/delete actions
  const [approvingGoalId, setApprovingGoalId] = useState(null);
  const [rejectingGoalId, setRejectingGoalId] = useState(null);
  const [deletingGoalId, setDeletingGoalId] = useState(null);

  const [isSavingGoals, setIsSavingGoals] = useState(false);
  const [isSubmittingGoals, setIsSubmittingGoals] = useState(false);
  const [hasModified, setHasModified] = useState(false);
  const [isGoalsSectionExpanded, setIsGoalsSectionExpanded] = useState(true);

  // Check if manager is viewing team member's goals (not own goals)
  // If targetUserId is null, try to get it from the first goal's userId
  const actualTargetUserId = useMemo(() => {
    if (targetUserId) return targetUserId;
    // Try to get userId from goals
    const firstGoal = goals.find(g => !g.isNewGoal && !g.isEmptyRow);
    if (firstGoal) {
      if (firstGoal.userId) {
        if (typeof firstGoal.userId === 'object' && firstGoal.userId._id) {
          return firstGoal.userId._id.toString();
        } else if (typeof firstGoal.userId === 'string') {
          return firstGoal.userId;
        }
      }
    }
    return null;
  }, [targetUserId, goals]);

  const isViewingTeamMemberGoals = useMemo(() => {
    const result = isManager && actualTargetUserId && currentUserId && actualTargetUserId.toString() !== currentUserId.toString();
    console.log('🔍 isViewingTeamMemberGoals check:', {
      isManager,
      targetUserId,
      actualTargetUserId,
      currentUserId,
      targetUserIdStr: targetUserId?.toString(),
      actualTargetUserIdStr: actualTargetUserId?.toString(),
      currentUserIdStr: currentUserId?.toString(),
      areDifferent: actualTargetUserId?.toString() !== currentUserId?.toString(),
      result
    });
    return result;
  }, [isManager, actualTargetUserId, currentUserId, targetUserId]);

  // Auth bypass: Org Admin in autonomous mode ONLY for their own goals
  const isAdminAutonomous = useMemo(() => {
    return Boolean(isOrgAdmin && isAutonomousMode && !isViewingTeamMemberGoals);
  }, [isOrgAdmin, isAutonomousMode, isViewingTeamMemberGoals]);

  // Check if all goals are approved (excluding new/empty rows)
  const allGoalsApproved = useMemo(() => {
    const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
    if (actualGoals.length === 0) return false;

    // Check if minimum goals requirement is met
    const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;

    // Check if all goals are approved
    const allApproved = actualGoals.every(g => {
      const status = (g.status || '').toString().trim().toUpperCase();
      return status === 'APPROVED' || status === 'COMPLETED';
    });

    // Both conditions must be true: minimum goals met AND all approved
    const result = meetsMinGoals && allApproved;

    // Debug logs
    console.log('🔍 Score Column Debug:', {
      isManager,
      isViewingTeamMemberGoals,
      allGoalsApproved: result,
      meetsMinGoals,
      allApproved,
      actualGoalsCount: actualGoals.length,
      minGoalsRequired: minGoals,
      goalsStatuses: actualGoals.map(g => ({
        id: g.id || g._id,
        status: g.status,
        statusUpper: (g.status || '').toString().trim().toUpperCase()
      })),
      targetUserId,
      currentUserId,
      targetUserIdStr: targetUserId?.toString(),
      currentUserIdStr: currentUserId?.toString(),
      areDifferent: targetUserId?.toString() !== currentUserId?.toString()
    });

    return result;
  }, [goals, isManager, isViewingTeamMemberGoals, targetUserId, currentUserId, minGoals]);

  // Check if goals are evaluated (any approved goal has a score)
  const areGoalsEvaluated = useMemo(() => {
    const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
    if (actualGoals.length === 0) return false;

    // Check if any approved goal has a score (meaning goals have been evaluated)
    const approvedGoals = actualGoals.filter(g => {
      const status = (g.status || '').toString().trim().toUpperCase();
      return status === 'APPROVED' || status === 'COMPLETED';
    });

    // If there are approved goals, check if any has a score
    if (approvedGoals.length === 0) return false;

    // Goals are evaluated if any approved goal has a score
    const hasEvaluatedGoal = approvedGoals.some(g =>
      g.score != null && g.score !== undefined && g.score !== ''
    );

    return hasEvaluatedGoal;
  }, [goals]);

  // Check if we're in evaluation window
  const isInEvaluationWindow = isEvaluationWindowActive;

  // Whether the SCORE column is currently the active call-to-action
  // (i.e. evaluator can input scores). Used to highlight the column header & cells
  // so reviewers visually notice it's their turn to add scores.
  const isScoringActive = useMemo(() => {
    if (!onSetAchievedPercent) return false;
    if (!allGoalsApproved) return false;
    if (areGoalsEvaluated) return false;
    return (
      (isViewingTeamMemberGoals && isEvaluationWindowActive) ||
      isAdminAutonomous
    );
  }, [onSetAchievedPercent, allGoalsApproved, areGoalsEvaluated, isViewingTeamMemberGoals, isEvaluationWindowActive, isAdminAutonomous]);

  // Check if we're in submission window
  const isInSubmissionWindow = isSubmissionWindowActive;

  // Check if goals are already submitted
  // Check actual submitted status (without autonomous mode bypass) - for autonomous mode button disable logic
  const actualGoalsSubmittedStatus = useMemo(() => {
    // Only check PENDING and APPROVED goals - these are the ones that get submittedAt set
    // REJECTED and COMPLETED goals don't get submittedAt, so we exclude them from the check
    const actualGoals = goals.filter(g =>
      !g.isNewGoal &&
      !g.isEmptyRow &&
      (g.status === 'PENDING' || g.status === 'APPROVED')
    );
    if (actualGoals.length === 0) return false;
    // Check if ALL PENDING/APPROVED goals have submittedAt set (not just some)
    // This ensures that if a new goal is added after submission, the flag becomes false
    const allGoalsSubmitted = actualGoals.every(g =>
      g.submittedAt !== null &&
      g.submittedAt !== undefined
    );
    return allGoalsSubmitted;
  }, [goals]);

  // Goals are considered submitted only if ALL existing goals have submittedAt set
  // If a new goal is added (without submittedAt), then goals are not fully submitted
  // True if any non-rejected goal has been edited after its last submission and
  // the manager hasn't yet re-reviewed it. We use this to re-open Submit for
  // Approval so the user can push the latest edits.
  const hasEditedGoals = useMemo(() => {
    return goals.some(g =>
      !g.isNewGoal &&
      !g.isEmptyRow &&
      g.editedSinceSubmit === true &&
      g.status !== 'APPROVED' &&
      g.status !== 'COMPLETED'
    );
  }, [goals]);

  const areGoalsSubmitted = useMemo(() => {
    // If org admin is in autonomous mode, check actual submitted status
    // BUT still allow if goals were modified (new goal added)
    if (isAdminAutonomous) {
      // If anything was modified, enable submission again
      if (hasModified) return false;
      // Edits to already-submitted goals also re-open submission.
      if (hasEditedGoals) return false;
      return actualGoalsSubmittedStatus;
    }

    // If anything was modified, enable submission again
    if (hasModified) return false;
    if (hasEditedGoals) return false;

    return actualGoalsSubmittedStatus;
  }, [goals, isAdminAutonomous, hasModified, hasEditedGoals, actualGoalsSubmittedStatus]);

  // Check if all goals have been saved (have submittedAt) - for "Save Goals" button disable logic
  const areAllGoalsSaved = useMemo(() => {
    const actualGoals = goals.filter(g =>
      !g.isNewGoal &&
      !g.isEmptyRow &&
      (g.status === 'PENDING' || g.status === 'APPROVED')
    );
    if (actualGoals.length === 0) return false;
    // All goals must have submittedAt to be considered saved
    return actualGoals.every(g => g.submittedAt !== null && g.submittedAt !== undefined);
  }, [goals]);

  // True if this user has any REJECTED goal in the current cycle. While the
  // review window is open, this re-opens submission for them so they can fix
  // the rejected goals and resubmit.
  const hasRejectedGoals = useMemo(() => {
    return goals.some(g => !g.isNewGoal && !g.isEmptyRow && g.status === 'REJECTED');
  }, [goals]);

  // Track if inlineNewGoal exists (not its content) to detect when it's created/cleared
  const inlineNewGoalExists = inlineNewGoal !== null;
  const prevExistsRef = useRef(inlineNewGoalExists);
  const isInternalUpdateRef = useRef(false);

  // Use ref to maintain stable order of empty rows
  const emptyRowKeysRef = useRef(new Map());

  // Track individual empty row input states to prevent order changes during typing
  const emptyRowInputsRef = useRef(new Map());

  // Additional new goal rows added via "+ Add Goal" button
  const [additionalNewRows, setAdditionalNewRows] = useState([]);
  const additionalRowCounterRef = useRef(0);

  // Track editing goal changes
  const prevEditingGoalIdRef = useRef(editingGoalId);
  useEffect(() => {
    if (editingGoalId && prevEditingGoalIdRef.current !== editingGoalId) {
      // Find the goal being edited
      const goalToEdit = goals.find(g => (g.id === editingGoalId || g._id === editingGoalId));
      if (goalToEdit) {
        // Initialize editing state (weight multiple of 10)
        const rawWeight = goalToEdit.weight ?? goalToEdit.weightage ?? minWeightage;
        setEditingGoalState({
          id: editingGoalId,
          name: goalToEdit.name || '',
          metric: goalToEdit.metric || '',
          weight: normalizeWeight(rawWeight),
          critical: goalToEdit.critical || false,
          projectId: goalToEdit.projectId?._id || goalToEdit.projectId || '',
        });
      }
      prevEditingGoalIdRef.current = editingGoalId;
    } else if (!editingGoalId && prevEditingGoalIdRef.current) {
      setEditingGoalState(null);
      prevEditingGoalIdRef.current = null;
    }
  }, [editingGoalId, goals, minWeightage, normalizeWeight]);

  // Only sync from parent when inlineNewGoal is first created or cleared (not on every update)
  useEffect(() => {
    // Only update if inlineNewGoal was created or cleared, and it wasn't our internal update
    if (prevExistsRef.current !== inlineNewGoalExists && !isInternalUpdateRef.current) {
      // inlineNewGoal was created or cleared - sync local state
      if (inlineNewGoal) {
        // Always start with fresh empty state when creating new goal
        setLocalInputState({
          name: '',
          metric: '= 100%',
          weight: minWeightage,
          critical: false,
          projectId: '',
        });
      } else {
        // Clear state when inlineNewGoal is cleared
        setLocalInputState({
          name: '',
          metric: '= 100%',
          weight: minWeightage,
          critical: false,
          projectId: '',
        });
      }
      prevExistsRef.current = inlineNewGoalExists;
    }
    // Reset the flag after effect runs
    isInternalUpdateRef.current = false;
  }, [inlineNewGoalExists, inlineNewGoal, minWeightage]);

  // Update only local state - sync to parent only when saving
  const updateLocalState = useCallback((updates) => {
    setLocalInputState(prev => ({ ...prev, ...updates }));
  }, []);

  // Sync local state to parent (called only on blur or save, NOT on every keystroke)
  const syncToParent = useCallback(() => {
    if (setInlineNewGoal) {
      isInternalUpdateRef.current = true; // Mark as internal update to prevent useEffect loop
      const base = inlineNewGoal || { name: '', metric: '= 100%', weight: minWeightage, critical: false, status: 'PENDING' };
      // Use current local state value
      setLocalInputState(current => {
        setInlineNewGoal({ ...base, ...current });
        return current; // Don't change local state
      });
    }
  }, [setInlineNewGoal, inlineNewGoal, minWeightage]);

  const getScoreFromAchieved = (achievedPercent) => {
    if (achievedPercent == null || isNaN(achievedPercent)) return 0;
    const p = Math.max(0, Math.min(100, achievedPercent));

    // If achievedPercent is 0, return 0
    if (p === 0) return 0;

    // Direct reverse mapping: achievedPercent = score * 10, so score = achievedPercent / 10
    // This preserves float values (e.g. 65% → 6.5, 44% → 4.4)
    return Math.round((p / 10) * 100) / 100;
  };

  const getStatusColor = (status) => {
    if (status === 'COMPLETED' || status === 'Approved') return 'success';
    if (status === 'REJECTED') return 'error';
    if (status === 'PENDING') return 'warning';
    return 'default';
  };

  // Memoize columns to prevent re-creation on every render
  const columns = useMemo(() => ([
    {
      title: 'CRITICAL',
      dataIndex: 'critical',
      key: 'focus',
      width: 100,
      align: 'center',
      fixed: 'left',
      render: (_, record) => {
        const isInlineNew = record.isNewGoal && !record.isEmptyRow;
        const isEmptyRow = record.isEmptyRow;
        const isEditingThisRow = !isInlineNew && !isEmptyRow && isGoalsCycleEditable && editingGoalId === record.id;

        // Read current critical state per row type
        let currentRowIsCritical = false;
        if (isInlineNew) {
          currentRowIsCritical = !!localInputState.critical;
        } else if (isEmptyRow && record.key) {
          const rowState = emptyRowInputsRef.current.get(record.key);
          currentRowIsCritical = !!(rowState?.critical);
        } else {
          currentRowIsCritical = !!record.critical;
        }

        // Critical limit (count existing + empty rows + inline new if critical)
        const existingCriticalCount = goals.filter(g => g.critical === true).length;
        let pendingCriticalCount = 0;
        emptyRowInputsRef.current.forEach((rowState) => {
          if (rowState.critical === true) pendingCriticalCount++;
        });
        if (isInlineNew && localInputState.critical) pendingCriticalCount++;
        const totalCriticalCount = existingCriticalCount + pendingCriticalCount;
        const isCriticalLimitReached = criticalRequired > 0 && totalCriticalCount >= criticalRequired;

        // Editable rule
        const canToggleSaved = !!(onToggleCritical && (isGoalsCycleEditable || isAdminAutonomous));
        const isPendingRow = isInlineNew || isEmptyRow;
        const isToggleable = isPendingRow || isEditingThisRow || canToggleSaved;
        const isLockedByLimit = isCriticalLimitReached && !currentRowIsCritical;
        const isDisabled = !isToggleable || isLockedByLimit;

        const tooltipText = currentRowIsCritical
          ? 'Critical'
          : isLockedByLimit
            ? `Maximum ${criticalRequired} critical goal(s) allowed`
            : isToggleable
              ? 'Mark as Critical'
              : 'Critical state locked';

        const handleToggle = () => {
          if (isDisabled) return;
          const newCritical = !currentRowIsCritical;
          // Critical is just a flag — preserve whatever weight the user typed.
          const currentWeight = isInlineNew
            ? (Number(localInputState.weight) || minWeightage)
            : (isEmptyRow && record.key
              ? Number((emptyRowInputsRef.current.get(record.key) || {}).weight) || minWeightage
              : minWeightage);

          if (isInlineNew) {
            updateLocalState({ critical: newCritical, weight: currentWeight });
          } else if (isEmptyRow && record.key) {
            const currentState = emptyRowInputsRef.current.get(record.key) || {};
            emptyRowInputsRef.current.set(record.key, {
              ...currentState,
              critical: newCritical,
              weight: currentWeight,
            });
            setLocalInputState(prev => ({ ...prev }));
          } else if (canToggleSaved) {
            onToggleCritical(record.id, newCritical);
          }
        };

        return (
          <Tooltip title={tooltipText}>
            <button
              type="button"
              onClick={handleToggle}
              disabled={isDisabled}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                currentRowIsCritical
                  ? (isDark ? 'bg-amber-500/20 text-amber-400 shadow-sm' : 'bg-amber-100 text-amber-600 shadow-sm')
                  : isDisabled
                    ? (isDark ? 'text-white/10 cursor-not-allowed' : 'text-slate-200 cursor-not-allowed')
                    : (isDark ? 'text-white/25 hover:text-amber-400 hover:bg-amber-500/10' : 'text-slate-300 hover:text-amber-500 hover:bg-amber-50')
              }`}
            >
              <Award
                size={18}
                strokeWidth={currentRowIsCritical ? 3 : 2}
                fill={currentRowIsCritical ? 'currentColor' : 'none'}
              />
            </button>
          </Tooltip>
        );
      },
    },
    // AI Goal column — only rendered when aiGoalEnabled is true
    ...(aiGoalEnabled ? [{
      title: 'AI GOAL',
      dataIndex: 'isAiGoal',
      key: 'aiGoal',
      width: 100,
      align: 'center',
      fixed: 'left',
      render: (_, record) => {
        const isInlineNew = record.isNewGoal && !record.isEmptyRow;
        const isEmptyRow = record.isEmptyRow;
        // New/empty rows can't be AI Goal yet (must be saved first)
        if (isInlineNew || isEmptyRow) {
          return (
            <Tooltip title="Save this goal first, then mark it as AI Goal">
              <button type="button" disabled className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? 'text-white/10 cursor-not-allowed' : 'text-slate-200 cursor-not-allowed'}`}>
                <BrainCircuit size={18} strokeWidth={2} />
              </button>
            </Tooltip>
          );
        }

        const currentIsAiGoal = !!record.isAiGoal;

        // Only ONE AI Goal per user+cycle. Once one is marked, the other icons
        // lock — same UX as Critical hitting its limit.
        const aiGoalCount = goals.filter(g => g.isAiGoal === true).length;
        const isAiLimitReached = aiGoalCount >= 1;

        const canToggleSaved = !!(onToggleAiGoal && (isGoalsCycleEditable || isAdminAutonomous));
        const isLockedByLimit = isAiLimitReached && !currentIsAiGoal;
        const isDisabled = !canToggleSaved || isLockedByLimit;

        const tooltipAi = currentIsAiGoal
          ? 'AI Goal (click to unmark)'
          : isLockedByLimit
            ? 'Only one AI Goal allowed — unmark the current one first'
            : canToggleSaved
              ? 'Mark as AI Goal'
              : 'AI Goal locked';

        return (
          <Tooltip title={tooltipAi}>
            <button
              type="button"
              onClick={() => { if (!isDisabled) onToggleAiGoal(record.id, !currentIsAiGoal); }}
              disabled={isDisabled}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                currentIsAiGoal
                  ? (isDark ? 'bg-violet-500/20 text-violet-400 shadow-sm' : 'bg-violet-100 text-violet-600 shadow-sm')
                  : isDisabled
                    ? (isDark ? 'text-white/10 cursor-not-allowed' : 'text-slate-200 cursor-not-allowed')
                    : (isDark ? 'text-white/25 hover:text-violet-400 hover:bg-violet-500/10' : 'text-slate-300 hover:text-violet-500 hover:bg-violet-50')
              }`}
            >
              <BrainCircuit
                size={18}
                strokeWidth={currentIsAiGoal ? 3 : 2}
                fill={currentIsAiGoal ? 'currentColor' : 'none'}
              />
            </button>
          </Tooltip>
        );
      },
    }] : []),
    {
      title: 'NO.',
      key: 'rowIndex',
      width: 70,
      align: 'center',
      fixed: 'left',
      render: (_, record, index) => (
        <span className={`text-[14px] font-bold tabular-nums ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
          {index + 1}
        </span>
      ),
    },
    {
      title: 'GOAL',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text, record) => {
        if (record.isNewGoal || record.isEmptyRow) {
          const isInlineNew = !record.isEmptyRow;

          // For empty rows, use individual state stored in ref (per row)
          // For inline new goal, use localInputState
          let inputValue = '';
          if (isInlineNew) {
            inputValue = localInputState.name || '';
          } else if (record.isEmptyRow && record.key) {
            // Get value from ref for this specific empty row
            const rowState = emptyRowInputsRef.current.get(record.key);
            inputValue = rowState?.name || '';
          }

          // Ensure value never exceeds 500 characters
          const displayValue = inputValue.length > 500 ? inputValue.slice(0, 500) : inputValue;

          return (
            <div className="flex flex-col gap-1.5 w-full max-w-full sm:max-w-[420px]">
              <Input
                key={`name-input-${record.key || 'new-goal'}`}
                placeholder="Goal name"
                value={displayValue}
                maxLength={500}
                onChange={(e) => {
                  let newValue = e.target.value;

                  // Always truncate to 500 characters first
                  if (newValue.length > 500) {
                    newValue = newValue.slice(0, 500);
                    toast.error('Goal name should be within 500 characters');
                  }

                  // Update with (possibly truncated) value
                  if (isInlineNew) {
                    // Only update local state - NO parent update during typing
                    updateLocalState({ name: newValue });
                  } else if (record.isEmptyRow && record.key) {
                    // Store in ref for this specific empty row
                    // This prevents order changes during typing
                    const currentState = emptyRowInputsRef.current.get(record.key) || {};
                    emptyRowInputsRef.current.set(record.key, {
                      ...currentState,
                      name: newValue,
                    });
                    // Force re-render by updating localInputState (but this won't change order)
                    // Use a dummy update to trigger re-render without affecting tableData
                    setLocalInputState(prev => ({ ...prev }));
                  }
                }}
                onKeyDown={(e) => {
                  // Prevent input if already at 500 characters (except backspace, delete, arrow keys, etc.)
                  const currentLength = displayValue.length;
                  if (currentLength >= 500 &&
                    e.key !== 'Backspace' &&
                    e.key !== 'Delete' &&
                    !e.key.startsWith('Arrow') &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    e.key.length === 1) {
                    e.preventDefault();
                    toast.error('Goal name should be within 500 characters');
                  }
                }}
                onPaste={(e) => {
                  // Handle paste event to truncate pasted content
                  e.preventDefault();
                  const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                  let newValue = displayValue + pastedText;

                  if (newValue.length > 500) {
                    newValue = newValue.slice(0, 500);
                    toast.error('Goal name should be within 500 characters');
                  }

                  // Update with (possibly truncated) value
                  if (isInlineNew) {
                    updateLocalState({ name: newValue });
                  } else if (record.isEmptyRow && record.key) {
                    const currentState = emptyRowInputsRef.current.get(record.key) || {};
                    emptyRowInputsRef.current.set(record.key, {
                      ...currentState,
                      name: newValue,
                    });
                    setLocalInputState(prev => ({ ...prev }));
                  }
                }}
                onBlur={() => {
                  // Sync to parent only when user leaves the field
                  if (isInlineNew) {
                    syncToParent();
                  }
                }}
                size="small"
                className={`!text-[16px] !h-9 !rounded-lg !border ${isDark
                  ? '!bg-transparent !border-transparent hover:!bg-white/[0.04] hover:!border-white/10 focus:!bg-white/[0.06] focus:!border-blue-400/40'
                  : '!bg-transparent !border-transparent hover:!bg-blue-50/40 hover:!border-blue-100 focus:!border-blue-400 focus:!bg-white'
                  }`}
                autoFocus={isInlineNew && !inputValue}
              />
              {/* Legacy Critical checkbox — replaced by FOCUS column award toggle */}
              {false && (
              <div className="flex items-center gap-1.5">
                {(() => {
                  // Get current row's critical state
                  let currentRowIsCritical = false;
                  if (isInlineNew) {
                    // For inline new goal, use localInputState
                    currentRowIsCritical = localInputState.critical;
                  } else if (record.isEmptyRow && record.key) {
                    // For empty rows, get from ref
                    const rowState = emptyRowInputsRef.current.get(record.key);
                    currentRowIsCritical = rowState?.critical || false;
                  } else {
                    // For existing goals, use record.critical
                    currentRowIsCritical = record.critical || false;
                  }

                  // Calculate current critical goals count
                  // Count existing goals that are critical
                  const existingCriticalCount = goals.filter(g => g.critical === true).length;

                  // Count empty rows that are critical (from ref)
                  let emptyRowsCriticalCount = 0;
                  emptyRowInputsRef.current.forEach((rowState) => {
                    if (rowState.critical === true) {
                      emptyRowsCriticalCount++;
                    }
                  });

                  // If inline new goal is critical, add 1
                  if (isInlineNew && localInputState.critical) {
                    emptyRowsCriticalCount++;
                  }

                  // Total critical count = existing + empty rows (including inline new if critical)
                  const currentCriticalCount = existingCriticalCount + emptyRowsCriticalCount;

                  // Check if critical limit is reached (maximum)
                  const isCriticalLimitReached = currentCriticalCount >= criticalRequired && criticalRequired > 0;

                  // Disable if limit is reached AND this goal is not currently critical
                  const isCriticalDisabled = isCriticalLimitReached && !currentRowIsCritical;

                  const isChecked = currentRowIsCritical;

                  return (
                    <Checkbox
                      checked={isChecked}
                      disabled={isCriticalDisabled}
                      onChange={(e) => {
                        if (!isCriticalDisabled) {
                          const newCritical = e.target.checked;
                          // Critical is just a flag — preserve the user's typed weight.
                          const currentWeight = isInlineNew
                            ? (Number(localInputState.weight) || minWeightage)
                            : (record.isEmptyRow && record.key
                              ? Number((emptyRowInputsRef.current.get(record.key) || {}).weight) || minWeightage
                              : minWeightage);
                          if (isInlineNew) {
                            updateLocalState({ critical: newCritical, weight: currentWeight });
                          } else if (record.isEmptyRow && record.key) {
                            const currentState = emptyRowInputsRef.current.get(record.key) || {};
                            emptyRowInputsRef.current.set(record.key, {
                              ...currentState,
                              critical: newCritical,
                              weight: currentWeight,
                            });
                            setLocalInputState(prev => ({ ...prev }));
                          }
                        }
                      }}
                      onBlur={() => {
                        // Sync to parent when user leaves the field
                        if (isInlineNew) {
                          syncToParent();
                        }
                      }}
                      className="!text-[10px] !m-0"
                    >
                      <span className={`text-[10px] font-bold ${isDark ? 'text-white/80' : 'text-slate-600'}`}>Critical</span>
                    </Checkbox>
                  );
                })()}
              </div>
              )}
            </div>
          );
        }
        if (isGoalsCycleEditable && editingGoalId === record.id) {
          const editValue = editingGoalState?.name ?? text;
          // Ensure value never exceeds 500 characters
          const displayValue = editValue && editValue.length > 500 ? editValue.slice(0, 500) : (editValue || '');

          return (
            <Input
              key={`edit-name-${record.id}`}
              value={displayValue}
              maxLength={500}
              onChange={(e) => {
                let newValue = e.target.value;

                // Always truncate to 500 characters first
                if (newValue.length > 500) {
                  newValue = newValue.slice(0, 500);
                  toast.error('Goal name should be within 500 characters');
                }

                // Update with (possibly truncated) value
                // Only update local editing state - NO parent update during typing
                setEditingGoalState(prev => prev ? { ...prev, name: newValue } : null);
              }}
              onKeyDown={(e) => {
                // Prevent input if already at 500 characters (except backspace, delete, arrow keys, etc.)
                const currentLength = displayValue.length;
                if (currentLength >= 500 &&
                  e.key !== 'Backspace' &&
                  e.key !== 'Delete' &&
                  !e.key.startsWith('Arrow') &&
                  !e.ctrlKey &&
                  !e.metaKey &&
                  e.key.length === 1) {
                  e.preventDefault();
                  toast.error('Goal name should be within 500 characters');
                }
              }}
              onPaste={(e) => {
                // Handle paste event to truncate pasted content
                e.preventDefault();
                const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                let newValue = displayValue + pastedText;

                if (newValue.length > 500) {
                  newValue = newValue.slice(0, 500);
                  toast.error('Goal name should be within 500 characters');
                }

                // Update with (possibly truncated) value
                setEditingGoalState(prev => prev ? { ...prev, name: newValue } : null);
              }}
              onBlur={() => {
                // Sync to parent when user leaves the field
                if (editingGoalState && onEdit) {
                  onEdit(record.id, { ...record, ...editingGoalState });
                }
              }}
              size="small"
              className={`!text-[16px] !w-full !max-w-full sm:!max-w-[220px] !h-9 !rounded-lg !border ${isDark
                ? '!bg-transparent !border-transparent hover:!bg-white/[0.04] hover:!border-white/10 focus:!bg-white/[0.06] focus:!border-blue-400/40'
                : '!bg-transparent !border-transparent hover:!bg-blue-50/40 hover:!border-blue-100 focus:!border-blue-400 focus:!bg-white'
                }`}
            />
          );
        }
        // Get manager name from createdBy field
        const getManagerName = () => {
          if (!record.addedByManager || !record.createdBy) {
            return null;
          }

          // Handle both object and string formats
          if (typeof record.createdBy === 'object' && record.createdBy !== null) {
            const firstName = record.createdBy.firstName || '';
            const lastName = record.createdBy.lastName || '';
            if (firstName || lastName) {
              return `${firstName} ${lastName}`.trim();
            }
          }

          return null;
        };

        const managerName = getManagerName();
        // Enforce goals setting: max critical goals = criticalRequired (e.g. 1 = only 1 allowed)
        const existingCriticalCount = goals.filter(g => g.critical === true).length;
        const isCriticalLimitReachedForFlame = criticalRequired > 0 && existingCriticalCount >= criticalRequired && !record.critical;
        const isFlameDisabled = (!isGoalsCycleEditable && !isAdminAutonomous) || isCriticalLimitReachedForFlame;

        // Tooltip title: show full text + manager name if added by manager
        const tooltipTitle = record.addedByManager && managerName
          ? `${text}\n\nAdded by ${managerName}`
          : record.addedByManager
            ? `${text}\n\nAdded by Manager`
            : text;

        return (
          <div className="flex flex-col gap-0.5 w-full max-w-full sm:max-w-[420px] min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Tooltip
                title={tooltipTitle}
                overlayStyle={{ maxWidth: '350px', whiteSpace: 'pre-line' }}
                overlayInnerStyle={{
                  maxWidth: '350px',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-line'
                }}
              >
                <span
                  className={`text-[13px] font-semibold block min-w-0 ${record.addedByManager ? 'cursor-help' : ''} ${record.italic
                    ? `italic font-medium ${isDark ? 'text-white/60' : 'text-slate-400'}`
                    : isDark ? 'text-white' : 'text-slate-800'
                    }`}
                  style={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }}
                >
                  {text}
                </span>
              </Tooltip>
              {/* Legacy inline critical Target icon — replaced by FOCUS column award toggle */}
              {false && (
              <Tooltip title={
                record.critical
                  ? 'Critical Goal'
                  : isCriticalLimitReachedForFlame
                    ? `Maximum ${criticalRequired} critical goal(s) allowed (Goals Settings)`
                    : 'Mark as Critical'
              } className="flex-shrink-0">
                <Button
                  type="text"
                  size="small"
                  className="!p-0 !min-w-0 !h-auto !border-none !bg-transparent group/critical"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCriticalLimitReachedForFlame) return;
                    if (onToggleCritical && (isGoalsCycleEditable || isAdminAutonomous)) {
                      onToggleCritical(record.id, !record.critical);
                    }
                  }}
                  disabled={isFlameDisabled}
                >
                  <Target
                    size={16}
                    className={`transition-all duration-300 ${record.critical
                      ? (isDark ? "text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.4)]" : "text-red-600 drop-shadow-[0_0_8px_rgba(220,38,38,0.2)]")
                      : (isDark ? "text-white/10 hover:text-red-400" : "text-slate-300 hover:text-red-500")
                      } group-hover/critical:scale-125`}
                  />
                </Button>
              </Tooltip>
              )}
            </div>
            {record.critical && !record.isNewGoal && !record.isEmptyRow && (
              <span className={`text-[10px] font-bold uppercase tracking-tight leading-none mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-500'}`}>
                Main objective
              </span>
            )}
            {record.addedByManager && (
              <span className={`text-[10px] font-medium italic leading-none ${isDark ? 'text-blue-400' : 'text-blue-500'
                }`}>
                {managerName ? `Added by ${managerName}` : 'Added by Manager'}
              </span>
            )}
            {record.managerNotes && (
              <span
                className={`text-[9px] font-medium leading-snug mt-0.5 truncate block min-w-0 ${isDark ? 'text-white/60' : 'text-slate-500'
                  }`}
                title={record.managerNotes}
              >
                Manager notes: {record.managerNotes}
              </span>
            )}
            {(record.status || '').toString().toUpperCase() === 'REJECTED' && record.rejectionReason && (
              <Tooltip
                title={record.rejectionReason}
                overlayStyle={{ maxWidth: '350px', whiteSpace: 'pre-line' }}
                overlayInnerStyle={{ maxWidth: '350px', wordBreak: 'break-word', whiteSpace: 'pre-line' }}
              >
                <span
                  className={`text-[10px] font-semibold leading-snug mt-0.5 truncate block min-w-0 cursor-help ${isDark ? 'text-rose-300' : 'text-rose-600'}`}
                >
                  Rejected: {record.rejectionReason}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'PROJECT',
      dataIndex: 'projectId',
      key: 'projectId',
      width: 160,
      render: (projectData, record) => {
        // For new goal rows (inline add or empty rows)
        if (record.isNewGoal || record.isEmptyRow) {
          const isInlineNew = !record.isEmptyRow;

          let selectedProjectId = '';
          if (isInlineNew) {
            selectedProjectId = localInputState.projectId || '';
          } else if (record.isEmptyRow && record.key) {
            const rowState = emptyRowInputsRef.current.get(record.key);
            selectedProjectId = rowState?.projectId || '';
          }

          return (
            <Select
              key={`project-input-${record.key || 'new-goal'}`}
              placeholder="Select"
              value={selectedProjectId || undefined}
              onChange={(value) => {
                if (isInlineNew) {
                  updateLocalState({ projectId: value });
                  // Sync to parent on change
                  setTimeout(() => syncToParent(), 0);
                } else if (record.isEmptyRow && record.key) {
                  const currentState = emptyRowInputsRef.current.get(record.key) || {};
                  emptyRowInputsRef.current.set(record.key, {
                    ...currentState,
                    projectId: value,
                  });
                  setLocalInputState(prev => ({ ...prev }));
                }
              }}
              size="small"
              className={`!text-[14px] !w-full !h-8`}
              style={{ width: '100%' }}
              options={projectsList.map(p => ({ label: p.name, value: p._id }))}
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              allowClear
            />
          );
        }

        // Edit mode - show dropdown
        if (isGoalsCycleEditable && editingGoalId === record.id) {
          const editProjectId = editingGoalState?.projectId ?? (projectData?._id || projectData || '');
          return (
            <Select
              key={`edit-project-${record.id}`}
              value={editProjectId || undefined}
              onChange={(value) => {
                setEditingGoalState(prev => prev ? { ...prev, projectId: value } : null);
              }}
              size="small"
              className={`!text-[14px] !w-full !h-8`}
              style={{ width: '100%' }}
              options={projectsList.map(p => ({ label: p.name, value: p._id }))}
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              allowClear
              placeholder="Select"
            />
          );
        }

        // Read-only display
        const projectName = projectData?.name || (typeof projectData === 'string' ? '' : 'General Project');
        return (
          <span className={`text-[14px] font-bold tracking-tight ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
            {projectName}
          </span>
        );
      },
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span>METRIC</span>
          <Tooltip
            title={
              <div className="text-sm">
                <p className="font-semibold mb-2">Allowed inputs:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Numbers (e.g., 85, 100)</li>
                  <li>Operators: &lt;, &gt;, =</li>
                  <li>Percentage: % (e.g., 85%)</li>
                  <li>Examples: = 100%, &gt; 90, &lt; 50%</li>
                </ul>
              </div>
            }
            color="#1f2937"
            overlayStyle={{ color: '#ffffff' }}
          >
            <Info size={16} className="cursor-help text-gray-400 hover:text-gray-600" />
          </Tooltip>
        </div>
      ),
      dataIndex: 'metric',
      key: 'metric',
      width: 150,
      render: (text, record) => {
        if (record.isNewGoal || record.isEmptyRow) {
          const isInlineNew = !record.isEmptyRow;

          // Get metric value based on row type
          let inputValue = '';
          if (isInlineNew) {
            inputValue = localInputState.metric;
          } else if (record.isEmptyRow && record.key) {
            const rowState = emptyRowInputsRef.current.get(record.key);
            inputValue = rowState?.metric ?? (record.metric ?? '');
          }

          return (
            <Input
              key={`metric-input-${record.key || 'new-goal'}`}
              placeholder="Metric"
              value={inputValue}
              onChange={(e) => {
                const newValue = e.target.value;

                // Filter out invalid characters (only allow numbers, <, >, =, %, and spaces)
                const filteredValue = newValue.replace(/[^<>=\s\d%]/g, '');

                // Show error only if characters were actually filtered
                if (filteredValue !== newValue && newValue.length > 0) {
                  toast.error('Metric should only contain numbers, <, >, =, and %', { id: 'metric-validation' });
                }

                // Cap numeric value at 100
                const numMatch = filteredValue.match(/\d+(\.\d+)?/);
                if (numMatch && parseFloat(numMatch[0]) > 100) {
                  toast.error('Metric value cannot exceed 100', { id: 'metric-validation' });
                  return;
                }

                if (isInlineNew) {
                  updateLocalState({ metric: filteredValue });
                } else if (record.isEmptyRow && record.key) {
                  const currentState = emptyRowInputsRef.current.get(record.key) || {};
                  emptyRowInputsRef.current.set(record.key, {
                    ...currentState,
                    metric: filteredValue,
                  });
                  setLocalInputState(prev => ({ ...prev }));
                }
              }}
              onBlur={() => {
                // Validate pattern on blur: optional operator (>, <, =, >=, <=) + number + optional %
                const val = (isInlineNew ? localInputState.metric : emptyRowInputsRef.current.get(record.key)?.metric) || '';
                const isValidPattern = /^(>=?|<=?|=)?\s*\d+(\.\d+)?\s*%?$/.test(val.trim());
                if (val.trim() && !isValidPattern) {
                  toast.error('Invalid metric format. Examples: > 50%, <= 100, = 75%, 80%', { id: 'metric-validation' });
                }
                const numMatch2 = val.trim().match(/\d+(\.\d+)?/);
                if (numMatch2 && parseFloat(numMatch2[0]) > 100) {
                  toast.error('Metric value cannot exceed 100', { id: 'metric-validation' });
                }
                if (isInlineNew) {
                  syncToParent();
                }
              }}
              size="small"
              className={`!text-[14px] !w-full !max-w-full sm:!max-w-[120px] !h-8 !rounded-lg !border ${isDark
                ? '!bg-transparent !border-transparent hover:!bg-white/[0.04] hover:!border-white/10 focus:!bg-white/[0.06] focus:!border-blue-400/40'
                : '!bg-transparent !border-transparent hover:!bg-blue-50/40 hover:!border-blue-100 focus:!border-blue-400 focus:!bg-white'
                }`}
            />
          );
        }
        if (isGoalsCycleEditable && editingGoalId === record.id) {
          const editValue = editingGoalState?.metric ?? text;
          return (
            <Input
              key={`edit-metric-${record.id}`}
              value={editValue}
              onChange={(e) => {
                let newValue = e.target.value;

                // Filter out invalid characters (only allow numbers, <, >, =, %, and spaces)
                const filteredValue = newValue.replace(/[^<>=\s\d%]/g, '');

                if (filteredValue !== newValue && newValue.length > 0) {
                  toast.error('Metric should only contain numbers, <, >, =, and %', { id: 'metric-validation' });
                }

                // Cap numeric value at 100
                const numMatch = filteredValue.match(/\d+(\.\d+)?/);
                if (numMatch && parseFloat(numMatch[0]) > 100) {
                  toast.error('Metric value cannot exceed 100', { id: 'metric-validation' });
                  return;
                }

                setEditingGoalState(prev => prev ? { ...prev, metric: filteredValue } : null);
              }}
              onBlur={() => {
                const val = editingGoalState?.metric || '';
                const isValidPattern = /^(>=?|<=?|=)?\s*\d+(\.\d+)?\s*%?$/.test(val.trim());
                if (val.trim() && !isValidPattern) {
                  toast.error('Invalid metric format. Examples: > 50%, <= 100, = 75%, 80%', { id: 'metric-validation' });
                  return; // Block save when metric is invalid
                }
                const numMatch2 = val.trim().match(/\d+(\.\d+)?/);
                if (numMatch2 && parseFloat(numMatch2[0]) > 100) {
                  toast.error('Metric value cannot exceed 100', { id: 'metric-validation' });
                  return;
                }
                if (editingGoalState && onEdit) {
                  onEdit(record.id, { ...record, ...editingGoalState });
                }
              }}
              size="small"
              className={`!text-[14px] !w-full !max-w-full sm:!max-w-[120px] !rounded-lg !border !h-8 ${isDark
                ? '!bg-transparent !border-transparent hover:!bg-white/[0.04] hover:!border-white/10 focus:!bg-white/[0.06] focus:!border-blue-400/40'
                : '!bg-transparent !border-transparent hover:!bg-blue-50/40 hover:!border-blue-100 focus:!border-blue-400 focus:!bg-white'
                }`}
            />
          );
        }
        return (
          <span className={`text-[14px] font-bold tracking-tight ${isDark ? 'text-white/60' : 'text-slate-300'
            }`}>
            {text}
          </span>
        );
      },
    },
    {
      title: isStarMode ? 'POINTS' : 'WEIGHTAGE',
      dataIndex: 'weight',
      key: 'weight',
      width: 120,
      render: (value, record) => {
        // Star flow: the employee never enters weightage — the manager assigns
        // points during review. Owner's view is read-only.
        if (isStarMode && !isViewingTeamMemberGoals && !isAdminAutonomous) {
          if (record.isNewGoal || record.isEmptyRow) {
            return (
              <Tooltip title="Your manager assigns points to this goal during review.">
                <span className={`text-[10px] font-bold ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                  Manager assigns
                </span>
              </Tooltip>
            );
          }
          const w = typeof value === 'string' ? parseInt(value, 10) : Number(value);
          return (
            <span className={`text-[15px] font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {w > 0 ? `${w}` : '—'}
            </span>
          );
        }
        if (record.isNewGoal || record.isEmptyRow) {
          const isInlineNew = !record.isEmptyRow;

          // Allow user to fully clear the field; only normalize on blur if a value exists.
          let inputValue = '';
          if (isInlineNew) {
            inputValue = localInputState.weight ?? '';
          } else if (record.isEmptyRow && record.key) {
            const rowState = emptyRowInputsRef.current.get(record.key);
            inputValue = rowState?.weight ?? record.weight ?? '';
          }

          const isEmpty = inputValue === '' || inputValue == null;
          const numericValue = isEmpty ? NaN : (typeof inputValue === 'number' ? inputValue : parseInt(inputValue, 10));
          const isOutOfRange = !isEmpty && !isNaN(numericValue) && (numericValue < minWeightage || numericValue > maxWeightage);
          const isNotMultipleOf10 = !isStarMode && !isEmpty && !isNaN(numericValue) && !isOutOfRange && numericValue % 10 !== 0;
          const errorMsg = isEmpty
            ? 'Required'
            : !isNaN(numericValue) && numericValue < minWeightage
              ? `Min ${minWeightage}%`
              : !isNaN(numericValue) && numericValue > maxWeightage
                ? `Max ${maxWeightage}%`
                : isNotMultipleOf10
                  ? 'Multiple of 10'
                  : '';
          const showError = isEmpty || isOutOfRange || isNotMultipleOf10;
          return (
            <div className="flex flex-col items-center gap-0.5">
              <div className="flex items-center gap-1">
                <Input
                  key={`weight-input-${record.key || 'new-goal'}`}
                  type="number"
                  status={showError ? 'error' : undefined}
                  min={minWeightage}
                  max={maxWeightage}
                  step={10}
                  value={inputValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    const next = v === '' ? '' : (isNaN(parseInt(v, 10)) ? '' : parseInt(v, 10));
                    if (isInlineNew) {
                      updateLocalState({ weight: next });
                    } else if (record.isEmptyRow && record.key) {
                      const currentState = emptyRowInputsRef.current.get(record.key) || {};
                      emptyRowInputsRef.current.set(record.key, { ...currentState, weight: next });
                      setLocalInputState(prev => ({ ...prev }));
                    }
                  }}
                  onBlur={() => {
                    if (isEmpty) return; // leave empty so user sees the Required hint
                    const normalized = normalizeWeight(inputValue);
                    if (isInlineNew) {
                      setLocalInputState(prev => ({ ...prev, weight: normalized }));
                      if (setInlineNewGoal) {
                        const base = inlineNewGoal || { name: '', metric: '= 100%', weight: minWeightage, critical: false, status: 'PENDING' };
                        setInlineNewGoal({ ...base, ...localInputState, weight: normalized });
                      }
                    } else if (record.isEmptyRow && record.key) {
                      const currentState = emptyRowInputsRef.current.get(record.key) || {};
                      emptyRowInputsRef.current.set(record.key, { ...currentState, weight: normalized });
                      setLocalInputState(prev => ({ ...prev }));
                    }
                  }}
                  size="small"
                  className={`!text-[15px] !font-bold !text-center !w-14 !h-9 !rounded-lg !border !px-1 ${showError
                    ? (isDark ? '!bg-rose-500/10 !border-rose-500/40 !text-rose-300' : '!bg-rose-50 !border-rose-300 !text-rose-600')
                    : (isDark
                      ? '!bg-transparent !border-transparent focus:!border-blue-400/40 !text-white'
                      : '!bg-transparent !border-transparent focus:!border-blue-400 !text-slate-900')
                    }`}
                  style={showError ? {
                    backgroundColor: isDark ? 'rgba(244, 63, 94, 0.1)' : '#fff1f2',
                    borderColor: isDark ? 'rgba(244, 63, 94, 0.4)' : '#fda4af',
                    color: isDark ? '#fda4af' : '#e11d48',
                  } : undefined}
                />
                <span
                  className="text-[13px] font-bold"
                  style={{ color: showError ? (isDark ? '#fb7185' : '#e11d48') : (isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8') }}
                >%</span>
              </div>
              {showError && (
                <span
                  className="text-[10px] font-bold whitespace-nowrap"
                  style={{ color: isDark ? '#fb7185' : '#e11d48' }}
                >
                  {errorMsg}
                </span>
              )}
            </div>
          );
        }
        if (isGoalsCycleEditable && editingGoalId === record.id) {
          const editValue = editingGoalState?.weight ?? (typeof value === 'string' ? parseInt(value.replace('%', ''), 10) : value);
          const displayWeight = editingGoalState?.weight != null
            ? editingGoalState.weight
            : (typeof editValue === 'number' ? editValue : (editValue ?? ''));
          const isEmptyEdit = displayWeight === '' || displayWeight == null;
          const numericEdit = isEmptyEdit ? NaN : (typeof displayWeight === 'number' ? displayWeight : parseInt(displayWeight, 10));
          const isOutOfRangeEdit = !isEmptyEdit && !isNaN(numericEdit) && (numericEdit < minWeightage || numericEdit > maxWeightage);
          const isNotMultipleOf10Edit = !isStarMode && !isEmptyEdit && !isNaN(numericEdit) && !isOutOfRangeEdit && numericEdit % 10 !== 0;
          const errorMsgEdit = isEmptyEdit
            ? 'Required'
            : !isNaN(numericEdit) && numericEdit < minWeightage
              ? `Min ${minWeightage}%`
              : !isNaN(numericEdit) && numericEdit > maxWeightage
                ? `Max ${maxWeightage}%`
                : isNotMultipleOf10Edit
                  ? 'Multiple of 10'
                  : '';
          const showErrorEdit = isEmptyEdit || isOutOfRangeEdit || isNotMultipleOf10Edit;
          return (
            <div className="flex flex-col items-center gap-0.5">
              <div className="flex items-center gap-1">
                <Input
                  key={`edit-weight-${record.id}`}
                  type="number"
                  status={showErrorEdit ? 'error' : undefined}
                  min={minWeightage}
                  max={maxWeightage}
                  step={10}
                  value={displayWeight}
                  onChange={(e) => {
                    const v = e.target.value;
                    const newWeight = v === '' ? '' : (isNaN(parseInt(v, 10)) ? '' : parseInt(v, 10));
                    setEditingGoalState(prev => prev ? { ...prev, weight: newWeight } : null);
                  }}
                  onBlur={() => {
                    if (isEmptyEdit) return; // keep empty so Required label stays visible
                    if (editingGoalState && onEdit) {
                      const normalized = normalizeWeight(editingGoalState.weight);
                      setEditingGoalState(prev => prev ? { ...prev, weight: normalized } : null);
                      onEdit(record.id, { ...record, ...editingGoalState, weight: normalized });
                    }
                  }}
                  size="small"
                  className={`!text-[15px] !font-bold !text-center !w-14 !h-9 !rounded-lg !border !px-1 ${showErrorEdit
                    ? (isDark ? '!bg-rose-500/10 !border-rose-500/40 !text-rose-300' : '!bg-rose-50 !border-rose-300 !text-rose-600')
                    : (isDark
                      ? '!bg-transparent !border-transparent focus:!border-blue-400/40 !text-white'
                      : '!bg-transparent !border-transparent focus:!border-blue-400 !text-slate-900')
                    }`}
                  style={showErrorEdit ? {
                    backgroundColor: isDark ? 'rgba(244, 63, 94, 0.1)' : '#fff1f2',
                    borderColor: isDark ? 'rgba(244, 63, 94, 0.4)' : '#fda4af',
                    color: isDark ? '#fda4af' : '#e11d48',
                  } : undefined}
                />
                <span
                  className="text-[13px] font-bold"
                  style={{ color: showErrorEdit ? (isDark ? '#fb7185' : '#e11d48') : (isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8') }}
                >%</span>
              </div>
              {showErrorEdit && (
                <span
                  className="text-[10px] font-bold whitespace-nowrap"
                  style={{ color: isDark ? '#fb7185' : '#e11d48' }}
                >
                  {errorMsgEdit}
                </span>
              )}
            </div>
          );
        }
        return (
          <span className={`text-[15px] font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {typeof value === 'string' ? value : `${value}%`}
          </span>
        );
      },
    },
    {
      title: (isStarMode || isChecklistMode) ? 'DONE?' : (isScoringActive ? (
        <span className="goals-score-header-active">
          <span className="goals-score-header-dot" />
          SCORE
        </span>
      ) : 'SCORE'),
      dataIndex: 'achieved',
      key: 'score',
      width: (isStarMode || isChecklistMode) ? 150 : 100,
      className: isScoringActive ? 'goals-score-cell-active' : '',
      onHeaderCell: () => isScoringActive ? { className: 'goals-score-header-cell-active' } : {},
      render: (value, record) => {
        if (record.isNewGoal || record.isEmptyRow) {
          return (
            <span className={`inline-flex items-center justify-center w-14 h-8 rounded-lg text-[10px] font-bold cursor-not-allowed select-none ${isDark ? 'bg-white/5 border border-white/10 text-white/60' : 'bg-slate-50 border border-slate-100 text-slate-300'
              }`}>
              —
            </span>
          );
        }

        const goalId = record.id || record._id;
        const achievedPercent = value || 0;
        const currentScore = getScoreFromAchieved(achievedPercent);
        const hasScore = achievedPercent > 0 && currentScore > 0;

        // Star / checklist flow: binary done / not done instead of a numeric score.
        // Employee self-marks; in star mode the manager then verifies.
        if (isStarMode || isChecklistMode) {
          const statusUpper = (record.status || '').toString().trim().toUpperCase();
          const claim = record.employeeCompleted;
          const verdict = record.managerVerified;
          const isApprovedGoal = statusUpper === 'APPROVED' || statusUpper === 'COMPLETED';
          const canSelfMark = !isViewingTeamMemberGoals && !!onSelfEvaluate &&
            (isChecklistMode || (isApprovedGoal && (isEvaluationWindowActive || isAdminAutonomous)));
          const canVerify = isStarMode && isViewingTeamMemberGoals && !!onVerifyGoal &&
            isApprovedGoal && (isEvaluationWindowActive || isAdminAutonomous);

          const btn = (label, active, tone, onClick, tooltip) => (
            <Tooltip title={tooltip}>
              <button
                type="button"
                onClick={onClick}
                className={`h-7 px-2.5 rounded-lg text-[10px] font-bold border transition-all active:scale-95 ${
                  active
                    ? (tone === 'good'
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-rose-500 text-white border-rose-500')
                    : (isDark
                      ? 'bg-white/5 text-white/60 border-white/10 hover:border-white/30'
                      : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400')
                }`}
              >
                {label}
              </button>
            </Tooltip>
          );

          if (canVerify) {
            // Manager verification: confirm or reject the employee's claim.
            const claimHint = claim === true ? 'Employee marked done' : claim === false ? 'Employee marked not done' : 'Not self-marked yet';
            const commentHint = record.employeeComment ? ` — "${record.employeeComment}"` : '';
            return (
              <div className="flex items-center gap-1.5">
                {btn(<Check size={13} strokeWidth={3} />, verdict === true, 'good',
                  () => onVerifyGoal(goalId, true), `Verify as done. ${claimHint}${commentHint}`)}
                {btn(<X size={13} strokeWidth={3} />, verdict === false, 'bad',
                  () => onVerifyGoal(goalId, false), `Mark as not done. ${claimHint}${commentHint}`)}
              </div>
            );
          }

          if (canSelfMark) {
            return (
              <div className="flex items-center gap-1.5">
                {btn('Done', claim === true, 'good',
                  () => onSelfEvaluate(goalId, true), 'Mark this goal as completed')}
                {btn('Not done', claim === false, 'bad',
                  () => onSelfEvaluate(goalId, false), 'Mark this goal as not completed')}
              </div>
            );
          }

          // Read-only: manager verdict wins over the employee's own claim.
          const state = verdict != null ? verdict : claim;
          if (state == null) {
            return (
              <span className={`text-[10px] font-bold ${isDark ? 'text-white/40' : 'text-slate-400'}`}>—</span>
            );
          }
          const verified = verdict != null;
          return (
            <Tooltip title={record.employeeComment || undefined}>
              <span className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] font-bold tracking-tight rounded-md border uppercase ${
                state
                  ? (isDark ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' : 'bg-emerald-50 text-[#16A34A] border-emerald-100')
                  : (isDark ? 'bg-rose-500/15 text-rose-300 border-rose-500/25' : 'bg-rose-50 text-rose-600 border-rose-100')
              }`}>
                {state ? <Check size={10} strokeWidth={3} /> : <X size={10} strokeWidth={3} />}
                {state ? 'Done' : 'Not done'}{verified ? ' ✓' : ''}
              </span>
            </Tooltip>
          );
        }

        // Manager can input/edit score for team member's goals when all goals are approved and evaluation window is active
        // OR org_admin in autonomous mode can evaluate their own goals
        // BUT not if goals are already evaluated (scores already set)
        // Score-only flow: the goal owner scores themselves continuously, no windows.
        const canEvaluate = isScoreOnlyMode
          ? (!isViewingTeamMemberGoals || isEvaluationWindowActive)
          : ((isViewingTeamMemberGoals && allGoalsApproved && isEvaluationWindowActive) ||
            (isAdminAutonomous && allGoalsApproved)) && !areGoalsEvaluated;
        if (canEvaluate && onSetAchievedPercent) {
          // Use scoreInputs if exists, otherwise use current score if it exists, otherwise empty
          const inputValue = scoreInputs[goalId] !== undefined
            ? (scoreInputs[goalId] === '' ? '' : scoreInputs[goalId])
            : (hasScore ? currentScore : '');

          return (
            <Input
              type="number"
              min={1}
              max={10}
              step={0.01}
              value={inputValue}
              onChange={(e) => {
                const inputVal = e.target.value;
                if (inputVal === '') {
                  setScoreInputs(prev => ({ ...prev, [goalId]: '' }));
                  return;
                }

                // Allow trailing dot for decimal entry (e.g. "4." while typing "4.4")
                if (inputVal.endsWith('.')) {
                  const numPart = parseFloat(inputVal);
                  if (!isNaN(numPart) && numPart >= 1 && numPart <= 10) {
                    setScoreInputs(prev => ({ ...prev, [goalId]: inputVal }));
                  }
                  return;
                }

                // Validate: Only allow up to 2 decimal places
                const decimalParts = inputVal.split('.');
                if (decimalParts.length > 1 && decimalParts[1].length > 2) {
                  // If more than 2 decimal places, truncate to 2
                  const truncated = parseFloat(inputVal).toFixed(2);
                  const score = parseFloat(truncated);
                  if (!isNaN(score) && score >= 1 && score <= 10) {
                    setScoreInputs(prev => ({ ...prev, [goalId]: truncated }));
                  }
                  return;
                }

                const score = parseFloat(inputVal);
                if (!isNaN(score) && score >= 1 && score <= 10) {
                  // Store raw string to preserve decimal input (e.g. "4.4" stays "4.4")
                  setScoreInputs(prev => ({ ...prev, [goalId]: inputVal }));
                }
              }}
              onBlur={async () => {
                // Score-only flow: commit immediately on blur — there's no
                // Evaluate button or evaluation window in this mode.
                if (!isScoreOnlyMode || !onSetAchievedPercent) return;
                const raw = scoreInputs[goalId];
                if (raw === undefined || raw === '') return;
                const score = parseFloat(raw);
                if (isNaN(score) || score < 1 || score > 10) return;
                try {
                  await onSetAchievedPercent(goalId, score * 10);
                  setScoreInputs(prev => {
                    const next = { ...prev };
                    delete next[goalId];
                    return next;
                  });
                } catch (e) {
                  console.error('Error saving score:', e);
                }
              }}
              size="small"
              className={`!text-[11px] !text-center !w-14 !h-8 !rounded-lg !border ${isDark
                ? '!bg-transparent !border-transparent hover:!bg-white/[0.04] hover:!border-white/10 focus:!bg-white/[0.06] focus:!border-blue-400/40'
                : '!bg-transparent !border-transparent hover:!bg-blue-50/40 hover:!border-blue-100 focus:!border-blue-400 focus:!bg-white'
                }`}
              placeholder="—"
            />
          );
        }

        // If score exists, show it as read-only
        if (hasScore) {
          return (
            <span className={`text-[12px] font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              {currentScore}
              <span className={`text-[10px] font-medium ml-0.5 ${isDark ? 'text-white/60' : 'text-slate-400'}`}>/10</span>
            </span>
          );
        }

        // Show disabled for manager viewing own goals or when goals are not all approved
        // BUT allow org_admin in autonomous mode to evaluate their own goals
        if (isManager && (!isViewingTeamMemberGoals || !allGoalsApproved) && !isAdminAutonomous) {
          return (
            <span className={`inline-flex items-center justify-center w-14 h-8 rounded-lg text-[10px] font-bold cursor-not-allowed select-none ${isDark ? 'bg-white/5 border border-white/10 text-white/60' : 'bg-slate-50 border border-slate-100 text-slate-300'
              }`}>
              —
            </span>
          );
        }

        // For employees, show disabled during editable period
        // BUT allow org_admin in autonomous mode to evaluate their own goals
        if (isGoalsCycleEditable && !isAdminAutonomous) {
          return (
            <span className={`inline-flex items-center justify-center w-14 h-8 rounded-lg text-[10px] font-bold cursor-not-allowed select-none ${isDark ? 'bg-white/5 border border-white/10 text-white/60' : 'bg-slate-50 border border-slate-100 text-slate-300'
              }`}>
              —
            </span>
          );
        }

        // Default: show score (0/10 if no score, otherwise actual score/10)
        return (
          <span className={`text-[12px] font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {currentScore || 0}
            <span className={`text-[10px] font-medium ml-0.5 ${isDark ? 'text-white/60' : 'text-slate-400'}`}>/10</span>
          </span>
        );
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status, record) => {
        if (record.isNewGoal || record.isEmptyRow) {
          return (
            <span className={`inline-flex items-center px-2 py-1 text-[9px] font-bold tracking-tight rounded-md border shadow-xs uppercase ${isDark ? 'bg-blue-500/15 text-blue-300 border-blue-500/25' : 'bg-blue-50 text-[#3B6EF8] border-blue-100'}`}>
              New
            </span>
          );
        }
        const statusUpper = (status || '').toString().trim().toUpperCase();
        // A PENDING goal without submittedAt has been saved locally but not yet
        // sent for approval — render it as "Saved" (neutral). Once Submit for
        // Approval runs, submittedAt is set and it flips to amber "Pending".
        const isSavedDraft = statusUpper === 'PENDING' && !record.submittedAt;
        const displayStatus = isSavedDraft ? 'SAVED' : statusUpper;
        // REJECTED always shows as "Rejected" in red regardless of cycle window state —
        // it's a manager action that the employee must always be able to recognize.
        let statusText;
        if (statusUpper === 'REJECTED') {
          statusText = 'Rejected';
        } else if (isGoalsCycleEditable) {
          statusText = isSavedDraft ? 'Saved' : status;
        } else {
          statusText = (statusUpper === 'COMPLETED' || statusUpper === 'APPROVED') ? 'Completed' : 'Not Completed';
        }

        let pillClass = '';
        if (displayStatus === 'APPROVED' || displayStatus === 'COMPLETED') {
          pillClass = isDark ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' : 'bg-emerald-50 text-[#16A34A] border-emerald-100';
        } else if (displayStatus === 'REJECTED') {
          pillClass = isDark ? 'bg-red-300 text-rose-300 border-rose-500/25' : 'bg-red-300 text-rose-600 border-rose-100';
        } else if (displayStatus === 'PENDING') {
          pillClass = isDark ? 'bg-amber-500/15 text-amber-300 border-amber-500/25' : 'bg-amber-50 text-amber-700 border-amber-100';
        } else if (displayStatus === 'SAVED') {
          pillClass = isDark ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' : 'bg-emerald-50 text-[#16A34A] border-emerald-100';
        } else {
          pillClass = isDark ? 'bg-white/5 text-white/50 border-white/10' : 'bg-slate-50 text-slate-500 border-slate-200';
        }

        const showEditedBadge = record.editedSinceSubmit === true && statusUpper !== 'APPROVED' && statusUpper !== 'COMPLETED';
        return (
          <span className="inline-flex items-center gap-1">
            <span className={`inline-flex items-center px-2 py-1 text-[9px] font-bold tracking-tight rounded-md border shadow-xs uppercase ${pillClass}`}>
              {statusText}
            </span>
            {showEditedBadge && (
              <span
                title="This goal was edited after submission. Click Submit for Approval to send the latest version to your manager."
                className={`inline-flex items-center px-2 py-1 text-[9px] font-bold tracking-tight rounded-md border shadow-xs uppercase ${isDark ? 'bg-purple-500/15 text-purple-300 border-purple-500/25' : 'bg-purple-50 text-purple-600 border-purple-200'}`}
              >
                Edited
              </span>
            )}
          </span>
        );
      },
    },
  ].filter(col => {
    if (col.key === 'weight' && isChecklistMode) return false; // no weightage in checklist mode
    return col.key !== 'score' || isEvaluationWindowActive || isStarMode || isChecklistMode || isScoreOnlyMode;
  })), [isDark, inlineNewGoal, localInputState, editingGoalId, editingGoalState, isGoalsCycleEditable, goals.length, minGoals, minWeightage, maxWeightage, normalizeWeight, updateLocalState, syncToParent, onEdit, onApprove, onReject, setEditingGoalId, onSaveNewGoal, onSaveEditGoal, onCancelEdit, getScoreFromAchieved, getStatusColor, isViewingTeamMemberGoals, allGoalsApproved, scoreInputs, onSetAchievedPercent, projectsList, isScoringActive, isEvaluationWindowActive, isStarMode, isChecklistMode, isScoreOnlyMode, onSelfEvaluate, onVerifyGoal, isAdminAutonomous, areGoalsEvaluated]);

  // Action column - add conditionally
  // Show action column for managers always, for employees only during editable period
  const actionColumn = useMemo(() => {
    console.log('🔍 Action Column Check:', {
      isGoalsCycleEditable,
      isManager,
      shouldShow: isGoalsCycleEditable || isManager,
      willReturnNull: !isGoalsCycleEditable && !isManager
    });

    if (!isGoalsCycleEditable && !isManager) {
      console.log('❌ Action Column NOT showing - condition failed');
      return null;
    }

    console.log('✅ Action Column WILL show');

    return {
      title: 'ACTION',
      key: 'action',
      width: 151,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        // Debug: Check if we're in action column render
        if (!isManager && record.id) {
          console.log('🔍 Action Column Render - Employee View:', {
            goalId: record.id,
            goalName: record.name,
            isManager,
            isNewGoal: record.isNewGoal,
            isEmptyRow: record.isEmptyRow,
            hasOnDelete: !!onDelete
          });
        }

        // Check if goal is already approved (needed for canEdit/canDelete logic)
        const goalStatus = (record.status || '').toString().trim().toUpperCase();
        const isApproved = goalStatus === 'APPROVED' || goalStatus === 'COMPLETED';

        // Manager permissions: Full CRUD access regardless of cycle status or constraints
        // Employee permissions: Can edit/delete when cycle is active (submission/review/evaluation), but not approve/evaluate
        // Employees can edit/delete their goals as long as submission window is active OR goal is rejected during review
        // They must NOT be already approved or completed
        const isEmployeeAllowedToEdit = isSubmissionWindowActive || (isReviewWindowActive && goalStatus === 'REJECTED');

        const canEdit = isAdminAutonomous || (isViewingTeamMember
          ? (isSubmissionWindowActive || isReviewWindowActive) && !isApproved
          : isGoalsCycleEditable && isEmployeeAllowedToEdit && !isApproved);

        const canDelete = isAdminAutonomous || (isViewingTeamMember
          ? (isSubmissionWindowActive || isReviewWindowActive) && !isApproved
          : isGoalsCycleEditable && isEmployeeAllowedToEdit && (goals.length > minGoals) && !isApproved);
        // Reject/Delete button: Manager can always reject, employee can't reject
        const canReject = isManager ? true : false; // Only managers can reject/delete

        // Check if this is the current user's own goal
        // Prevent managers from approving/rejecting their own goals
        let goalUserId = null;
        if (record.userId) {
          if (typeof record.userId === 'object' && record.userId._id) {
            goalUserId = record.userId._id.toString();
          } else if (typeof record.userId === 'string') {
            goalUserId = record.userId;
          } else if (record.userId != null) {
            goalUserId = record.userId.toString();
          }
        }
        const isOwnGoal = currentUserId && goalUserId && currentUserId.toString() === goalUserId.toString();

        // Org Admin can approve/reject their own goals in autonomous mode
        // Regular managers can only approve/reject team members' goals during the Review Window
        const canApprove = (isManager && !isOwnGoal && isReviewWindowActive) || (isAdminAutonomous && isOwnGoal);
        const canRejectOwn = (canReject && !isOwnGoal && isReviewWindowActive) || (isAdminAutonomous && isOwnGoal);
        // When manager viewing team member's goals but review window is closed, show friendly message instead of "Not authorized"
        const showReviewWindowMessage = isManager && !isOwnGoal && !isReviewWindowActive;
        const isApproving = approvingGoalId === record.id;
        const isRejecting = rejectingGoalId === record.id;
        const isDeleting = deletingGoalId === record.id;
        const isProcessing = isApproving || isRejecting || isDeleting;

        // Employee can delete their own goals - simple rule: if edit button shows, delete button should also show
        // For employees: if they can edit a goal, they can delete it (it's their goal)
        // Note: isManager might be true if user has reportees, but if they're viewing their own goals (isViewingTeamMemberGoals is false), they should be able to delete
        const canDeleteOwn = !isViewingTeamMemberGoals && // Not viewing team member's goals (viewing own goals)
          canEdit && // Same condition as edit button - if edit shows, delete should show
          onDelete; // Delete handler must exist

        // Manager can delete team member's goals during submission and review window
        // Check both isViewingTeamMemberGoals (computed) and isViewingTeamMember (prop) for safety
        const isViewingTeamMemberGoalsOrProp = isViewingTeamMemberGoals || isViewingTeamMember;
        const canDeleteTeamMemberGoal = isViewingTeamMemberGoalsOrProp && // Viewing team member's goals
          isManager && // User is a manager
          (isSubmissionWindowActive || isReviewWindowActive) && // In submission or review window
          !isApproved && // Goal is not approved
          onDelete; // Delete handler must exist

        // Debug log for delete permissions (always log for managers to debug)
        if (isManager) {
          console.log('🔍 [GoalsTable] Delete Permission Check:', {
            goalId: record.id,
            goalName: record.name,
            isViewingTeamMemberGoals,
            isViewingTeamMember, // Also check the prop
            isViewingTeamMemberGoalsOrProp,
            isManager,
            isSubmissionWindowActive,
            isReviewWindowActive,
            isApproved,
            hasOnDelete: !!onDelete,
            canDeleteTeamMemberGoal,
            canDeleteOwn,
            finalCanDelete: canDeleteOwn || canDeleteTeamMemberGoal,
            allConditions: {
              isViewingTeamMemberGoalsOrProp: isViewingTeamMemberGoalsOrProp,
              isManager: isManager,
              windowActive: isSubmissionWindowActive || isReviewWindowActive,
              notApproved: !isApproved,
              hasOnDelete: !!onDelete
            }
          });
        }

        if (record.isNewGoal || record.isEmptyRow) {
          return (
            <Space size="small" className="flex items-center">
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  // Get data to save based on row type
                  let dataToSave = null;

                  if (record.isEmptyRow && record.key) {
                    const rowState = emptyRowInputsRef.current.get(record.key);
                    const rawW = rowState?.weight;
                    if (rawW === '' || rawW == null) {
                      toast.error('Weightage is required.');
                      return;
                    }
                    const w = normalizeWeight(rawW);
                    dataToSave = {
                      name: rowState?.name || '',
                      metric: rowState?.metric || '= 100%',
                      weight: w,
                      critical: rowState?.critical || false,
                      projectId: rowState?.projectId || null,
                      status: 'PENDING'
                    };
                  } else {
                    syncToParent();
                    const rawW = localInputState.weight;
                    if (rawW === '' || rawW == null) {
                      toast.error('Weightage is required.');
                      return;
                    }
                    const w = normalizeWeight(rawW);
                    dataToSave = {
                      ...localInputState,
                      weight: w,
                      status: 'PENDING'
                    };
                  }

                  // Validation: Check if goal name is empty
                  if (!dataToSave.name || !dataToSave.name.trim()) {
                    toast.error('Goal name is required. Please enter a goal name before saving.');
                    return;
                  }
                  // Validation: Goal name must contain at least one letter
                  if (!/[a-zA-Z]/.test(dataToSave.name)) {
                    toast.error('Goal name must contain at least one letter. Only numbers or special characters are not allowed.');
                    return;
                  }
                  // Validation: metric format must be valid
                  const metricVal = (dataToSave.metric || '').trim();
                  if (metricVal && !/^(>=?|<=?|=)?\s*\d+(\.\d+)?\s*%?$/.test(metricVal)) {
                    toast.error('Invalid metric format. Examples: > 50%, <= 100, = 75%, 80%', { id: 'metric-validation' });
                    return;
                  }
                  // Validation: metric numeric value must not exceed 100
                  const metricNumMatch = metricVal.match(/\d+(\.\d+)?/);
                  if (metricNumMatch && parseFloat(metricNumMatch[0]) > 100) {
                    toast.error('Metric value cannot exceed 100', { id: 'metric-validation' });
                    return;
                  }
                  // Validation: weight must be multiple of 10 and within min/max
                  // (star mode: manager reassigns points later, any whole number in range is fine)
                  const w = dataToSave.weight ?? minWeightage;
                  if ((!isStarMode && w % 10 !== 0) || w < minWeightage || w > maxWeightage) {
                    toast.error(isStarMode
                      ? `Points must be between ${minWeightage} and ${maxWeightage}.`
                      : `Weightage must be a multiple of 10 between ${minWeightage} and ${maxWeightage}.`);
                    return;
                  }

                  // Save the goal (Validation moved to Submit Goals).
                  // Await the parent's save+refetch so the inline draft row stays visible
                  // until the persisted row replaces it — avoids the flicker where the row
                  // disappears for one render between local-clear and refetch-arrival.
                  if (onSaveNewGoal) {
                    Promise.resolve(onSaveNewGoal(dataToSave)).then(() => {
                      if (!record.isEmptyRow) {
                        // Parent (handleSaveNewGoal in MyGoals) already clears inlineNewGoal
                        // after the refetch — we only reset local input state here.
                        setLocalInputState({
                          name: '',
                          metric: '= 100%',
                          weight: minWeightage,
                          critical: false,
                        });
                      } else if (record.isEmptyRow && record.key) {
                        emptyRowInputsRef.current.delete(record.key);
                        setAdditionalNewRows(prev => prev.filter(k => k !== record.key));
                        setLocalInputState(prev => ({ ...prev }));
                      }
                    }).catch(() => {
                      // Parent rejected (e.g. backend validation like weightage > 100%).
                      // Keep the draft row + typed values so user can fix and retry.
                    });
                  }
                }}
                disabled={!isAdminAutonomous && !isSubmissionWindowActive && !isReviewWindowActive}
                className={`!text-[11px] !font-bold !h-8 !px-4 !rounded-lg !shadow-md !shadow-blue-500/15 active:!scale-95 transition-all ${(!isAdminAutonomous && !isSubmissionWindowActive && !isReviewWindowActive)
                  ? '!bg-slate-200 !text-slate-400 cursor-not-allowed !shadow-none border-none'
                  : '!bg-[#3B6EF8] hover:!bg-[#2F58C7] !text-white !border-[#3B6EF8]'
                  }`}
              >
                Done
              </Button>
              <Button
                type="text"
                size="small"
                icon={<X size={15} strokeWidth={2.5} />}
                onClick={() => {
                  if (record.isEmptyRow && record.key) {
                    // Clear empty row state from ref
                    emptyRowInputsRef.current.delete(record.key);
                    // Remove from additionalNewRows if it's an additional row
                    setAdditionalNewRows(prev => prev.filter(k => k !== record.key));
                    // Force re-render
                    setLocalInputState(prev => ({ ...prev }));
                  } else {
                    // For inline new goal, clear it
                    setInlineNewGoal && setInlineNewGoal(null);
                    setLocalInputState({
                      name: '',
                      metric: '= 100%',
                      weight: minWeightage,
                      critical: false,
                    });
                  }
                }}
                className={`!w-8 !h-8 !p-0 !flex !items-center !justify-center !rounded-lg !transition-all ${isDark
                  ? '!text-white/45 hover:!text-white/80 hover:!bg-white/10'
                  : '!text-[#94A3B8] hover:!text-[#64748B] hover:!bg-slate-100'
                  }`}
              />
            </Space>
          );
        }
        if (editingGoalId === record.id) {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                onClick={async () => {
                  // Validation: Check if goal name is empty
                  const goalName = editingGoalState?.name !== undefined
                    ? editingGoalState.name
                    : record.name;

                  if (!goalName || !goalName.trim()) {
                    toast.error('Goal name is required. Please enter a goal name before saving.');
                    return;
                  }
                  // Validation: Goal name must contain at least one letter
                  if (!/[a-zA-Z]/.test(goalName)) {
                    toast.error('Goal name must contain at least one letter. Only numbers or special characters are not allowed.');
                    return;
                  }

                  // Validation: metric format
                  const metricVal = (editingGoalState?.metric ?? record.metric ?? '').trim();
                  if (metricVal && !/^(>=?|<=?|=)?\s*\d+(\.\d+)?\s*%?$/.test(metricVal)) {
                    toast.error('Invalid metric format. Examples: > 50%, <= 100, = 75%, 80%', { id: 'metric-validation' });
                    return;
                  }

                  // Validation: weightage required
                  const editW = editingGoalState?.weight;
                  if (editingGoalState && (editW === '' || editW == null)) {
                    toast.error('Weightage is required.');
                    return;
                  }

                  // Sync editing state to parent before saving
                  if (editingGoalState && onEdit) {
                    onEdit(record.id, { ...record, ...editingGoalState });
                  }
                  if (onSaveEditGoal) {
                    await onSaveEditGoal(record.id);
                    setHasModified(true); // Enable re-submission
                  }
                }}
                disabled={!canEdit}
                className={`!text-[11px] !font-bold !h-8 !px-4 !rounded-lg !shadow-md !shadow-emerald-500/15 active:!scale-95 transition-all ${!canEdit
                  ? '!bg-slate-200 !text-slate-400 cursor-not-allowed !shadow-none border-none'
                  : '!bg-emerald-500 hover:!bg-emerald-600 !text-white !border-emerald-500'
                  }`}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setEditingGoalState(null);
                  onCancelEdit && onCancelEdit();
                }}
                className={`!text-[11px] !font-bold !h-8 !px-4 !rounded-lg active:!scale-95 transition-all ${isDark
                  ? '!bg-white/[0.04] !text-white/80 !border-white/15 hover:!bg-white/[0.08] hover:!text-white'
                  : '!bg-white !text-slate-700 !border-slate-200 hover:!bg-slate-50 hover:!text-slate-900'
                  }`}
              >
                Cancel
              </Button>
            </Space>
          );
        }
        // Debug logging for delete button - ALWAYS log for employees to see what's happening
        if (!isManager) {
          console.log('🔍 Delete Button Debug:', {
            goalId: record.id,
            goalName: record.name,
            isManager,
            canEdit,
            isOwnGoal,
            hasOnDelete: !!onDelete,
            currentUserId: currentUserId?.toString(),
            goalUserId: goalUserId?.toString(),
            canDeleteOwn,
            isNewGoal: record.isNewGoal,
            isEmptyRow: record.isEmptyRow,
            'allConditions': {
              '!isManager': !isManager,
              'canEdit': canEdit,
              'hasOnDelete': !!onDelete
            }
          });
        }

        // Handle approve with loading state
        const handleApprove = async () => {
          if (isApproved || isProcessing) return;
          setApprovingGoalId(record.id);
          try {
            await onApprove(record.id);
          } catch (error) {
            console.error('Error approving goal:', error);
          } finally {
            setApprovingGoalId(null);
          }
        };

        // Handle reject with loading state
        const handleReject = async () => {
          if (isApproved || isProcessing) return;
          setRejectingGoalId(record.id);
          try {
            await onReject(record.id);
          } catch (error) {
            console.error('Error rejecting goal:', error);
          } finally {
            setRejectingGoalId(null);
          }
        };

        // Handle delete with confirmation dialog
        const handleDelete = () => {
          if (isApproved || isProcessing) return;
          
          Modal.confirm({
            title: 'Delete Goal',
            content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            centered: true,
            className: isDark ? 'goals-dark-delete-modal' : '',
            styles: {
              content: {
                padding: '24px',
              },
              body: {
                fontSize: '14px',
              },
            },
            onOk: async () => {
              setDeletingGoalId(record.id);
              try {
                await onDelete(record.id);
                setHasModified(true); // Enable re-submission
              } catch (error) {
                console.error('Error deleting goal:', error);
              } finally {
                setDeletingGoalId(null);
              }
            },
          });
        };

        return (
          <Space size="small" className="tg-row-actions">
            {/* Approve button - only for managers, but not their own goals */}
            <Tooltip title={!canApprove ? (showReviewWindowMessage ? "You can approve the goal only when review window is open" : "Not authorized") : isApproved ? "Already Approved" : isApproving ? "Approving..." : "Approve"}>
              <Button
                type="text"
                icon={<Check size={15} strokeWidth={2.5} />}
                onClick={handleApprove}
                loading={isApproving}
                disabled={!canApprove || isApproved || isProcessing}
                className={`!w-8 !h-8 !p-0 !flex !items-center !justify-center !rounded-lg !transition-all ${!canApprove || isApproved
                  ? (isDark ? '!text-white/20 !cursor-not-allowed' : '!text-slate-200 !cursor-not-allowed')
                  : (isDark ? '!text-white/55 hover:!text-emerald-400 hover:!bg-emerald-500/15' : '!text-[#64748B] hover:!text-emerald-500 hover:!bg-emerald-50')
                }`}
              />
            </Tooltip>

            {/* Reject button - managers only; visible during submission window (disabled), active during review window */}
            <Tooltip title={!canRejectOwn ? (showReviewWindowMessage ? "You can reject the goal only when review window is open" : "Not authorized") : isApproved ? "Cannot reject approved goal" : isRejecting ? "Rejecting..." : "Reject Goal"}>
              <Button
                type="text"
                icon={<X size={15} strokeWidth={2.5} />}
                onClick={handleReject}
                loading={isRejecting}
                disabled={!canRejectOwn || isApproved || isProcessing}
                className={`!w-8 !h-8 !p-0 !flex !items-center !justify-center !rounded-lg !transition-all ${!canRejectOwn || isApproved
                  ? (isDark ? '!text-white/20 !cursor-not-allowed' : '!text-slate-200 !cursor-not-allowed')
                  : (isDark ? '!text-white/55 hover:!text-rose-400 hover:!bg-rose-500/15' : '!text-[#64748B] hover:!text-[#DC2626] hover:!bg-[#FEF2F2]')
                }`}
              />
            </Tooltip>

            {/* Edit button */}
            <Tooltip title={!canEdit ? "Not allowed" : isApproved ? "Cannot edit approved goal" : "Edit Goal"}>
              <Button
                type="text"
                icon={<Edit3 size={15} strokeWidth={2.5} />}
                onClick={() => canEdit && !isApproved && setEditingGoalId && setEditingGoalId(record.id)}
                disabled={!canEdit || isApproved}
                className={`!w-8 !h-8 !p-0 !flex !items-center !justify-center !rounded-lg !transition-all ${!canEdit || isApproved
                  ? (isDark ? '!text-white/20 !cursor-not-allowed' : '!text-slate-200 !cursor-not-allowed')
                  : (isDark ? '!text-white/55 hover:!text-blue-400 hover:!bg-blue-500/15' : '!text-[#64748B] hover:!text-[#3B6EF8] hover:!bg-[#EFF6FF]')
                }`}
              />
            </Tooltip>

            {/* Delete button (Trash icon) - for employees (own goals) or managers (team member goals during submission/review window) */}
            <Tooltip title={
              !canDeleteOwn && !canDeleteTeamMemberGoal
                ? "Not allowed"
                : isApproved
                  ? "Cannot delete approved goal"
                  : isDeleting
                    ? "Deleting..."
                    : "Delete Goal"
            }>
              <Button
                type="text"
                icon={<Trash2 size={15} strokeWidth={2.5} />}
                onClick={handleDelete}
                loading={isDeleting}
                disabled={(!canDeleteOwn && !canDeleteTeamMemberGoal) || isApproved || isProcessing}
                className={`!w-8 !h-8 !p-0 !flex !items-center !justify-center !rounded-lg !transition-all ${(!canDeleteOwn && !canDeleteTeamMemberGoal) || isApproved
                  ? (isDark ? '!text-white/20 !cursor-not-allowed' : '!text-slate-200 !cursor-not-allowed')
                  : (isDark ? '!text-white/55 hover:!text-rose-400 hover:!bg-rose-500/15' : '!text-[#64748B] hover:!text-[#DC2626] hover:!bg-[#FEF2F2]')
                }`}
              />
            </Tooltip>
          </Space>
        );
      },
    };
  }, [
    isDark,
    inlineNewGoal,
    localInputState,
    editingGoalId,
    editingGoalState,
    isGoalsCycleEditable,
    goals,
    minGoals,
    syncToParent,
    onEdit,
    onApprove,
    onReject,
    onDelete,
    setInlineNewGoal,
    setEditingGoalState,
    onSaveNewGoal,
    onSaveEditGoal,
    onCancelEdit,
    isManager,
    isViewingTeamMember,
    isViewingTeamMemberGoals,
    areGoalsSubmitted,
    isSubmissionWindowActive,
    isReviewWindowActive,
    currentUserId,
    approvingGoalId,
    rejectingGoalId,
    deletingGoalId,
    isAdminAutonomous,
    isDark,
  ]);

  // Combine base columns with action column
  const finalColumns = useMemo(() => {
    const cols = [...columns];
    if (actionColumn) {
      cols.push(actionColumn);
    }
    return cols;
  }, [columns, actionColumn]);

  // Memoize rowClassName function
  // `tg-datarow` reuses the app-wide hover CSS (index.css) so the action column
  // below (wrapped in `.tg-row-actions`) only shows on row hover, same pattern
  // as the Projects/Team tables.
  const rowClassNameFn = useCallback((record) => {
    if (record.isNewGoal || record.isEmptyRow) {
      return `tg-datarow ${isDark ? 'bg-blue-500/10' : 'bg-blue-50/30'}`;
    }
    if (isGoalsCycleEditable && editingGoalId === record.id) {
      return `tg-datarow ${isDark ? 'bg-blue-500/10' : 'bg-blue-50/20'}`;
    }
    return 'tg-datarow';
  }, [isDark, isGoalsCycleEditable, editingGoalId]);

  // Memoize table components
  const tableComponents = useMemo(() => ({
    body: {
      cell: (props) => {
        // Ensure proper vertical alignment for new goal row
        if (props.record?.isNewGoal || props.record?.isEmptyRow) {
          return <td {...props} className={`${props.className} align-middle`} />;
        }
        return <td {...props} />;
      }
    }
  }), []);

  // Memoize table data to prevent re-creation on every render
  const tableData = useMemo(() => {
    const data = [];

    // Count existing goals (excluding new goal row)
    const existingGoalsCount = goals.length;
    // No auto-generated empty placeholder rows — users add rows via the "Add New Goal Row" pill.
    // Min-goal validation still runs on Submit; this only suppresses pre-rendered empty rows.
    const emptyRowsNeeded = 0;

    // Add inline new goal row if it exists
    // Managers can add goals regardless of cycle status, employees only during submission window
    if (inlineNewGoal !== null && (isManager || isGoalsCycleEditable)) {
      data.push({
        ...inlineNewGoal,
        key: 'new-goal',
        index: -1,
        isNewGoal: true,
      });
    }

    // Add existing goals - sort critical goals to top
    const sortedGoals = [...goals].sort((a, b) => {
      const aCritical = a.critical ? 1 : 0;
      const bCritical = b.critical ? 1 : 0;
      return bCritical - aCritical; // critical (true) first
    });
    sortedGoals.forEach((goal, idx) => {
      data.push({
        ...goal,
        key: goal.id || goal._id || `goal-${idx}`,
        index: idx,
      });
    });

    // Add empty rows to meet minimum requirements
    // Use stable keys based on position (0, 1, 2...) that NEVER change
    // No pre-marked critical - user will decide which goals to mark as critical
    for (let i = 0; i < emptyRowsNeeded; i++) {
      // Generate stable key based on position only (0, 1, 2, etc.)
      // This key should NEVER change, even if existingGoalsCount changes
      const stableKey = `empty-row-pos-${i}`;

      // Store the stable key mapping only once
      if (!emptyRowKeysRef.current.has(stableKey)) {
        emptyRowKeysRef.current.set(stableKey, {
          position: i, // Position in empty rows (0, 1, 2, etc.)
          originalIndex: existingGoalsCount + i,
        });
      }

      // Initialize empty row state in ref if not already set (fresh empty state)
      if (!emptyRowInputsRef.current.has(stableKey)) {
        emptyRowInputsRef.current.set(stableKey, {
          name: '',
          metric: '= 100%',
          weight: minWeightage,
          critical: false, // Always start as non-critical, user decides
        });
      }

      data.push({
        key: stableKey,
        index: existingGoalsCount + i,
        isNewGoal: true,
        isEmptyRow: true,
        name: '',
        metric: '= 100%',
        weight: minWeightage,
        critical: false, // Always start as non-critical, user decides
        status: 'PENDING',
        _position: i, // Stable position for this empty row
      });
    }

    // Add additional new goal rows (added via "+ Add Goal" button)
    additionalNewRows.forEach((rowKey, i) => {
      if (!emptyRowInputsRef.current.has(rowKey)) {
        emptyRowInputsRef.current.set(rowKey, {
          name: '',
          metric: '= 100%',
          weight: minWeightage,
          critical: false,
        });
      }
      data.push({
        key: rowKey,
        index: goals.length + emptyRowsNeeded + i,
        isNewGoal: true,
        isEmptyRow: true,
        name: '',
        metric: '= 100%',
        weight: minWeightage,
        critical: false,
        status: 'PENDING',
      });
    });

    // Order: New goal (if exists) -> Critical goals -> Non-critical goals -> Empty rows -> Additional rows
    // Critical goals are sorted to top among existing goals
    // Note: localInputState is NOT in dependencies to prevent re-runs during typing
    return data;
  }, [goals, isGoalsCycleEditable, inlineNewGoal !== null, minGoals, criticalRequired, isManager, minWeightage, additionalNewRows]);

  // Check if there are unsaved goals - if yes, enable button
  // Must be defined AFTER tableData since it uses tableData
  const hasUnsavedGoals = useMemo(() => {
    return inlineNewGoal !== null || tableData.some(row => row.isEmptyRow) || additionalNewRows.length > 0;
  }, [inlineNewGoal, tableData, additionalNewRows]);

  // Compute goal stats for header display.
  // Single rule everywhere: sum of all goals must equal 100. Critical is just a flag.
  const actualGoalsCount = goals.filter(g => !g.isNewGoal && !g.isEmptyRow).length;
  const allWeightSum = goals.filter(g => !g.isNewGoal && !g.isEmptyRow).reduce((s, g) => s + (Number(g.weightage) || Number(g.weight) || 0), 0);
  // Header chip + progress-bar drive off this value.
  const nonCriticalWeightSum = allWeightSum;

  // Footer portal — when a #goals-cycle-footer-slot exists in the DOM (rendered by parent),
  // the submit footer renders there instead of inside the table card. Falls back to in-card
  // rendering for parents (e.g. ManagerView) that don't provide the slot.
  const [footerSlot, setFooterSlot] = useState(null);
  useEffect(() => {
    const el = document.getElementById('goals-cycle-footer-slot');
    setFooterSlot(el || null);
  }, []);
  const renderFooter = (footerNode) => footerSlot ? createPortal(footerNode, footerSlot) : footerNode;

  return (
    <motion.div
      className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-slate-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
      }`}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, type: "spring", stiffness: 100, damping: 15 }}
    >
      {/* Header Bar — legacy, hidden but preserved */}
      {false && (
      <div className={`px-5 py-3.5 flex items-center justify-between ${isDark ? 'border-b border-white/5' : 'border-b border-slate-100'}`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <h2 className={`text-[11px] font-semibold tracking-wide ${isDark ? 'text-white/50' : 'text-slate-400'}`}>
              SMART Goals
            </h2>
            <Tooltip overlayStyle={{maxWidth: 400}} title={<div style={{padding: '4px 2px'}}>
              <div style={{fontWeight: 600, marginBottom: 4, fontSize: 12}}>SMART Goals</div>
              <p style={{margin: '0 0 8px', fontSize: 11, opacity: 0.8}}>Specific, Measurable, Achievable, Relevant, Time-bound</p>
              <div style={{fontWeight: 600, marginBottom: 4, fontSize: 12}}>Badge Criteria</div>
              <ul style={{paddingLeft: 16, margin: 0, display: 'flex', flexDirection: 'column', gap: 3}}>
                {badgeRanges.length > 0 ? badgeRanges.map((range, i) => (
                  <li key={i} style={{fontSize: 11}}>
                    {range.lessThan
                      ? `Score < ${range.max}`
                      : `Score ${range.min} – ${range.max}`
                    } → <strong>{range.label}</strong>
                  </li>
                )) : (
                  <>
                    <li style={{fontSize: 11}}>Score &lt; 6 → <strong>Need Improvement</strong></li>
                    <li style={{fontSize: 11}}>Score 6 – 8 → <strong>Growing Employee</strong></li>
                    <li style={{fontSize: 11}}>Score 8.5 – 10 → <strong>Best Employee</strong></li>
                  </>
                )}
              </ul>
            </div>}>
              <Info size={13} className={`cursor-help ${isDark ? 'text-white/25' : 'text-slate-300'}`} />
            </Tooltip>
          </div>

          {/* Inline stats chips */}
          <div className="flex items-center gap-3">
            {/* Goals count & limit */}
            <div className={`flex items-center gap-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold ${isDark ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
              <span className={isDark ? 'text-white/50' : 'text-slate-500'}>GOALS:&nbsp;</span>
              <span className={`font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{actualGoalsCount}</span>
              <span className={`mx-2 ${isDark ? 'text-white/15' : 'text-slate-300'}`}>|</span>
              <span className={isDark ? 'text-white/50' : 'text-slate-500'}>LIMIT:&nbsp;</span>
              <span className={`font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{maxGoals > 0 ? maxGoals : '∞'}</span>
            </div>

            {/* Weight remaining */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold ${
              nonCriticalWeightSum === 100
                ? isDark ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' : 'text-emerald-600 bg-emerald-50 border border-emerald-200'
                : nonCriticalWeightSum > 100
                  ? isDark ? 'text-red-400 bg-red-500/10 border border-red-500/20 animate-attention-shake' : 'text-red-600 bg-red-50 border border-red-200 animate-attention-shake'
                  : isDark ? 'text-amber-400 bg-amber-500/10 border border-amber-500/20 animate-attention-shake' : 'text-amber-600 bg-amber-50 border border-amber-200 animate-attention-shake'
            }`}>
              <span>{Math.max(0, 100 - nonCriticalWeightSum)}% : Total Weightage</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {/* Weightage info - compact */}
          <Tooltip title={`Weightage per goal: ${minWeightage}% – ${maxWeightage}%. All goals must sum to 100%.`}>
            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] cursor-help ${isDark
              ? 'bg-blue-500/10 border border-blue-500/15'
              : 'bg-blue-50/80 border border-blue-100'
            }`}>
              <Info size={11} className={isDark ? 'text-blue-400' : 'text-blue-500'} />
              <span className={`font-semibold ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Weight</span>
              <span className={`font-bold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>{minWeightage}%</span>
              <span className={isDark ? 'text-white/20' : 'text-slate-300'}>–</span>
              <span className={`font-bold ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>{maxWeightage}%</span>
            </div>
          </Tooltip>

          <Tooltip title={
            areGoalsEvaluated
              ? "Cannot add goals after evaluation"
              : (maxGoals > 0 && tableData.length >= maxGoals ? `Maximum ${maxGoals} goals allowed` : "Add Goal")
          }>
            <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => {
              console.log('🎯 Add Goal Click Details:', {
                canAddGoal,
                setInlineNewGoal: !!setInlineNewGoal,
                isManager,
                areGoalsEvaluated,
                isSubmissionWindowActive,
                isReviewWindowActive,
                maxGoals,
                currentGoalsCount: goals.length,
                isMaxReached: maxGoals > 0 && goals.length >= maxGoals
              });
              const totalCurrentRows = tableData.length;
              if (canAddGoal && !areGoalsEvaluated && !(maxGoals > 0 && totalCurrentRows >= maxGoals)) {
                // Add a new row via additionalNewRows
                additionalRowCounterRef.current += 1;
                const newKey = `additional-row-${additionalRowCounterRef.current}`;
                // Initialize input state for the new row
                emptyRowInputsRef.current.set(newKey, {
                  name: '',
                  metric: '= 100%',
                  weight: minWeightage,
                  critical: false,
                });
                setAdditionalNewRows(prev => [...prev, newKey]);
                console.log('Added new goal row:', newKey);
              } else {
                console.warn('Cannot add goal:', { canAddGoal, areGoalsEvaluated });
              }
            }}
            disabled={
              !canAddGoal ||
              (maxGoals > 0 && tableData.length >= maxGoals) ||
              (isManager && areGoalsEvaluated) ||
              !isSubmissionWindowActive
            }
            onMouseEnter={() => {
              if (!canAddGoal || (maxGoals > 0 && goals.length >= maxGoals) || (isManager && areGoalsEvaluated) || !isSubmissionWindowActive) {
                console.log('🛰️ Add Goal Button Disabled Reasons:', {
                  isManager,
                  canAddGoal,
                  maxGoals,
                  currentGoalsCount: goals.length,
                  isMaxReached: maxGoals > 0 && goals.length >= maxGoals,
                  areGoalsEvaluated,
                  isSubmissionWindowActive,
                  isReviewWindowActive,
                  isWindowActive: isSubmissionWindowActive || isReviewWindowActive
                });
              }
            }}
            className="!text-[10px] !font-bold !uppercase !tracking-wider !h-8 !px-3.5 !rounded-lg !shadow-none"
          >
            Add Goal
          </Button>
        </Tooltip>
        </div>
      </div>
      )}

      {/* Mock-aligned Header Bar — clickable to collapse the goals table */}
      <div
        onClick={() => setIsGoalsSectionExpanded(prev => !prev)}
        className={`px-6 py-4 flex items-center justify-between cursor-pointer transition-colors group/sec ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50/60'} ${isGoalsSectionExpanded ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
            <Target size={18} />
          </div>
          <h3 className={`text-[14px] font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Qualitative &amp; Quantitative Goals
            <span className={`ml-2 text-[12px] font-normal ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              ({actualGoalsCount} active)
            </span>
          </h3>
          <span onClick={(e) => e.stopPropagation()}>
            <Tooltip overlayStyle={{maxWidth: 400}} title={<div style={{padding: '4px 2px'}}>
              <div style={{fontWeight: 600, marginBottom: 4, fontSize: 12}}>SMART Goals</div>
              <p style={{margin: '0 0 8px', fontSize: 11, opacity: 0.8}}>Specific, Measurable, Achievable, Relevant, Time-bound</p>
              <div style={{fontWeight: 600, marginBottom: 4, fontSize: 12}}>Weightage</div>
              <p style={{margin: '0 0 8px', fontSize: 11, opacity: 0.8}}>Per goal: {minWeightage}% – {maxWeightage}%. All goals must sum to 100%.</p>
            </div>}>
              <Info size={13} className={`cursor-help ${isDark ? 'text-white/30' : 'text-slate-300'}`} />
            </Tooltip>
          </span>
        </div>

        <div className="flex items-center gap-4">
          {/* <div className={`flex items-center h-7 px-3 rounded-full text-[10px] font-bold uppercase tracking-wider ${isDark ? 'bg-white text-slate-900' : 'bg-slate-900 text-white'}`}>
            {actualGoalsCount - (goals.filter(g => !g.isNewGoal && !g.isEmptyRow && (g.status === 'NEW' || g.status === 'PENDING')).length)} Saved
          </div> */}
          {isGoalsSectionExpanded
            ? <ChevronUp size={18} className={`${isDark ? 'text-white/40 group-hover/sec:text-white/70' : 'text-slate-400 group-hover/sec:text-slate-700'}`} />
            : <ChevronDown size={18} className={`${isDark ? 'text-white/40 group-hover/sec:text-white/70' : 'text-slate-400 group-hover/sec:text-slate-700'}`} />}
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin" style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
        <AnimatePresence initial={false}>
        {isGoalsSectionExpanded && (
        <motion.div
          key="goals-section-body"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
        {/* Single flat table — matches App.tsx mock (no Critical/Non-Critical partition).
            Critical-vs-non-critical distinction lives in the row itself (Main objective sub-label,
            weight cell rendering, and Submit-time validation), not as banner section headers. */}
        <Table
          columns={finalColumns}
          dataSource={tableData}
          pagination={false}
          size="small"
          className={`goals-table ${isDark ? 'ant-table-dark' : ''}`}
          rowClassName={rowClassNameFn}
          components={tableComponents}
          scroll={{ x: 'max-content' }}
          style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
        />

        {/* Mock-aligned Add Goal pill (footer of table) */}
        {(() => {
          const totalCurrentRows = tableData.length;
          const isAtMaxWeight = nonCriticalWeightSum >= 100;
          const isDisabled =
            !canAddGoal ||
            (maxGoals > 0 && totalCurrentRows >= maxGoals) ||
            (isManager && areGoalsEvaluated) ||
            !isSubmissionWindowActive ||
            isAtMaxWeight;
          return (
            <Tooltip title={
              areGoalsEvaluated
                ? "Cannot add goals after evaluation"
                : isAtMaxWeight
                  ? "Total weightage already 100%. Edit existing goals to free up weight before adding more."
                  : (maxGoals > 0 && totalCurrentRows >= maxGoals ? `Maximum ${maxGoals} goals allowed` : "Add a new goal row")
            }>
              <div className={`px-6 py-4 flex justify-center ${isDark ? 'bg-white/[0.02]' : 'bg-[#FBFDFF]'}`}>
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return;
                    additionalRowCounterRef.current += 1;
                    const newKey = `additional-row-${additionalRowCounterRef.current}`;
                    emptyRowInputsRef.current.set(newKey, {
                      name: '',
                      metric: '= 100%',
                      weight: minWeightage,
                      critical: false,
                    });
                    setAdditionalNewRows(prev => [...prev, newKey]);
                  }}
                  className={`group h-10 px-6 flex items-center justify-center gap-2 text-[13px] font-bold rounded-lg shadow-sm transition-all active:scale-[0.98] ${
                    isDisabled
                      ? (isDark ? 'text-white/30 bg-white/5 border border-white/10 cursor-not-allowed' : 'text-slate-300 bg-white border border-slate-100 cursor-not-allowed')
                      : (isDark
                        ? 'text-blue-400 bg-white/[0.04] border border-blue-500/30 hover:border-blue-400 hover:shadow-md'
                        : 'text-blue-600 bg-white border border-blue-200 hover:border-blue-500 hover:shadow-md')
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                    isDisabled
                      ? (isDark ? 'bg-white/5 text-white/30' : 'bg-slate-50 text-slate-300')
                      : (isDark ? 'bg-blue-500/15 text-blue-400 group-hover:bg-blue-500 group-hover:text-white' : 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white')
                  }`}>
                    <Plus size={14} strokeWidth={3} />
                  </span>
                  Add New Goal Row
                </button>
              </div>
            </Tooltip>
          );
        })()}
        </motion.div>
        )}
        </AnimatePresence>

        {/* Save Goals and Submit Goals Buttons - For employees and managers during submission window */}
        {(() => {
          const shouldShow = (isSubmissionWindowActive || isReviewWindowActive || isGoalsCycleEditable);
          console.log('🔍 Save/Submit Buttons Visibility:', {
            isManager,
            isInSubmissionWindow,
            isGoalsCycleEditable,
            areGoalsSubmitted,
            shouldShow,
            currentCycle: currentCycle ? {
              status: currentCycle.status,
              submissionStart: currentCycle.submissionStart,
              submissionEnd: currentCycle.submissionEnd
            } : null
          });
          return shouldShow;
        })() && renderFooter(
            <div className={`flex items-center justify-between gap-4 px-6 py-3.5 ${footerSlot ? '' : 'border-t'} ${isDark ? (footerSlot ? 'bg-[#0b1020]' : 'border-white/5 bg-white/[0.02]') : (footerSlot ? 'bg-white' : 'border-slate-100 bg-white')}`}>
              {/* Cycle Compliance — left-side compliance summary */}
              {(() => {
                const savedCount = actualGoalsCount;
                const minRequired = minGoals > 0 ? minGoals : 0;
                const maxAllowed = maxGoals > 0 ? maxGoals : null;
                const isOutOfRange = (minRequired > 0 && savedCount < minRequired) || (maxAllowed !== null && savedCount > maxAllowed);
                const weightSumNum = Number(nonCriticalWeightSum) || 0;
                const weightPct = Math.min(weightSumNum, 100);
                const isWeightOver = weightSumNum > 100;
                // Tolerate float drift (99.999… counted as full).
                const isWeightFull = weightSumNum >= 99.99 && weightSumNum <= 100.01;
                const goalsRangeLabel = maxAllowed
                  ? (minRequired > 0 ? `${minRequired}-${maxAllowed} Required` : `Up to ${maxAllowed}`)
                  : (minRequired > 0 ? `${minRequired}+ Required` : 'No limit');
                return (
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-white/45' : 'text-slate-500'}`}>
                      Cycle Compliance
                    </span>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`text-[12px] font-medium ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                        Goals:&nbsp;
                        <span className={`font-bold ${isOutOfRange ? (isDark ? 'text-rose-400' : 'text-rose-500') : (isDark ? 'text-white' : 'text-slate-900')}`}>
                          {savedCount}
                        </span>
                        <span className={`mx-1.5 ${isDark ? 'text-white/15' : 'text-slate-300'}`}>/</span>
                        <span className={isDark ? 'text-white/45' : 'text-slate-400'}>{goalsRangeLabel}</span>
                      </span>
                      {!isChecklistMode && <div className={`w-px h-3.5 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />}
                      {!isChecklistMode && <div className="flex items-center gap-2 w-44">
                        <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.06] border border-white/5' : 'bg-slate-100 border border-black/[0.04]'}`}>
                          <motion.div
                            initial={false}
                            animate={{ width: `${weightPct}%` }}
                            transition={{ duration: 0.4, ease: 'easeOut' }}
                            style={{
                              width: `${weightPct}%`,
                              background: isWeightOver
                                ? '#f43f5e'
                                : isWeightFull
                                  ? (isDark ? '#34D399' : '#10B981')
                                  : (isDark ? '#60a5fa' : '#2563eb'),
                            }}
                            className="h-full"
                          />
                        </div>
                        <span className={`text-[11px] font-bold tabular-nums min-w-[34px] text-right ${
                          isWeightOver ? (isDark ? 'text-rose-400' : 'text-rose-500') : (isDark ? 'text-white/85' : 'text-slate-900')
                        }`}>
                          {nonCriticalWeightSum}%
                        </span>
                      </div>}
                    </div>
                  </div>
                );
              })()}

              {/* Action buttons */}
              <div className="flex items-center gap-3 shrink-0">
              {/* Save Goals Button - Save all unsaved goals */}
              {/* <Tooltip title={
                (isAdminAutonomous ? actualGoalsSubmittedStatus : areGoalsSubmitted)
                  ? "Goals have already been submitted. Cannot save new goals."
                  : isAdminAutonomous
                    ? (inlineNewGoal !== null || tableData.some(row => row.isEmptyRow) ? "Save all unsaved goals" : "All goals are already saved")
                    : (!isSubmissionWindowActive && !isReviewWindowActive)
                      ? "Window is closed. Cannot save goals."
                      : (inlineNewGoal !== null || tableData.some(row => row.isEmptyRow))
                        ? "Save all unsaved goals"
                        : "All goals are already saved"
              }>
                <Button
                  type="default"
                  size="large"
                  loading={isSavingGoals}
                  disabled={
                    isAdminAutonomous ? (actualGoalsSubmittedStatus || isSavingGoals || isSubmittingGoals || (areAllGoalsSaved && !hasUnsavedGoals) || (inlineNewGoal === null && !tableData.some(row => row.isEmptyRow))) : (
                      isSavingGoals ||
                      isSubmittingGoals ||
                      (areAllGoalsSaved && !hasUnsavedGoals) ||
                      (!isSubmissionWindowActive && !isReviewWindowActive) ||
                      (inlineNewGoal === null && !tableData.some(row => row.isEmptyRow))
                    )
                  }
                  className={`!text-[12px] !font-semibold !h-9 !px-4 !rounded-lg ${(isAdminAutonomous ? (isSavingGoals || isSubmittingGoals || (areAllGoalsSaved && !hasUnsavedGoals) || (inlineNewGoal === null && !tableData.some(row => row.isEmptyRow))) : (
                    isSavingGoals ||
                    isSubmittingGoals ||
                    (areAllGoalsSaved && !hasUnsavedGoals) ||
                    (!isSubmissionWindowActive && !isReviewWindowActive) ||
                    (inlineNewGoal === null && !tableData.some(row => row.isEmptyRow))
                  ))
                    ? (isDark ? '!bg-white/5 !border-white/10 !text-white/40 !cursor-not-allowed' : '!bg-slate-50 !text-slate-300 !border-slate-100 !cursor-not-allowed')
                    : (isDark ? '!bg-white/5 !border-white/10 !text-white/80 hover:!bg-white/10' : '!bg-white !border-slate-200 !text-slate-700 hover:!bg-slate-50')
                    }`}
                  onClick={async () => {
                    setIsSavingGoals(true);
                    try {
                      // Save all unsaved goals
                      const unsavedRows = tableData.filter(row => row.isNewGoal || row.isEmptyRow);
                      const savePromises = [];

                      for (const row of unsavedRows) {
                        if (row.isEmptyRow && row.key) {
                          // For empty rows, get data from ref
                          const rowState = emptyRowInputsRef.current.get(row.key);
                          if (rowState?.name?.trim()) {
                            if (!/[a-zA-Z]/.test(rowState.name)) {
                              toast.error('Goal name must contain at least one letter. Only numbers or special characters are not allowed.');
                              return;
                            }
                            const dataToSave = {
                              name: rowState.name.trim(),
                              metric: rowState.metric || '= 100%',
                              weight: rowState.weight || 20,
                              critical: rowState.critical || false,
                              projectId: rowState.projectId || null,
                              status: 'PENDING'
                            };

                            if (onSaveNewGoal) {
                              savePromises.push(onSaveNewGoal(dataToSave));
                            }
                          }
                        } else if (!row.isEmptyRow) {
                          // For inline new goal, sync to parent first
                          syncToParent();
                          if (localInputState.name?.trim()) {
                            if (!/[a-zA-Z]/.test(localInputState.name)) {
                              toast.error('Goal name must contain at least one letter. Only numbers or special characters are not allowed.');
                              return;
                            }
                            const dataToSave = {
                              ...localInputState,
                              status: 'PENDING'
                            };

                            if (onSaveNewGoal) {
                              savePromises.push(onSaveNewGoal(dataToSave));
                            }
                          }
                        }
                      }

                      if (savePromises.length > 0) {
                        await Promise.all(savePromises);
                        setAdditionalNewRows([]);
                        setHasModified(true); // Enable re-submission
                        toast.success('All goals saved successfully!');
                      } else {
                        // No new goals to save
                        toast.info('No unsaved goals to save');
                      }
                    } catch (error) {
                      console.error('Error saving goals:', error);
                      toast.error(error.response?.data?.error?.message || 'Failed to save some goals');
                    } finally {
                      setIsSavingGoals(false);
                    }
                  }}
                >
                  Save Goals
                </Button>
              </Tooltip> */}

              {/* Submit Goals Button — only relevant during the Submission window
                  (or Review window if the employee has rejected goals to resubmit).
                  Hidden for managers viewing a team member's goals — they don't submit on behalf. */}
              {(!isViewingTeamMemberGoals || isAdminAutonomous) &&
                (isSubmissionWindowActive || (isReviewWindowActive && hasRejectedGoals) || isAdminAutonomous) && (
              <Tooltip title={
                isViewingTeamMemberGoals
                  ? null
                  : (isAdminAutonomous ? actualGoalsSubmittedStatus : areGoalsSubmitted)
                    ? "Goals have already been submitted"
                    : (!isAdminAutonomous && !isManager && !isSubmissionWindowActive && !(isReviewWindowActive && hasRejectedGoals))
                      ? "Submission window is closed. Goals can only be submitted during the submission window."
                      : (() => {
                        const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                        const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;
                        const criticalCount = actualGoals.filter(g => g.critical === true).length;
                        const meetsCriticalRequirement = criticalRequired === 0 || criticalCount >= criticalRequired;
                        const aiGoalCount = actualGoals.filter(g => g.isAiGoal === true).length;
                        const meetsAiGoalRequirement = !aiGoalEnabled || aiGoalCount === 1;

                        if (!meetsMinGoals) {
                          return `Minimum ${minGoals} goal(s) required. Currently ${actualGoals.length} goal(s) added.`;
                        }
                        if (!meetsCriticalRequirement) {
                          return `At least ${criticalRequired} critical goal(s) required. Currently ${criticalCount} critical goal(s).`;
                        }
                        if (!meetsAiGoalRequirement) {
                          return aiGoalCount === 0 ? 'Mark one goal as AI Goal before submitting.' : 'Only one goal can be marked as AI Goal.';
                        }
                        if (inlineNewGoal !== null || tableData.some(row => row.isEmptyRow)) {
                          return "Please save all goals before submitting";
                        }
                        return "Submit all goals to your manager for review";
                      })()
              }>
                <Button
                  type="primary"
                  size="large"
                  loading={isSubmittingGoals}
                  disabled={
                    isAdminAutonomous ? (
                      actualGoalsSubmittedStatus || // Disable if goals are already submitted
                      isSubmittingGoals ||
                      isSavingGoals ||
                      (() => {
                        const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                        const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;
                        const criticalCount = actualGoals.filter(g => g.critical === true).length;
                        const meetsCriticalRequirement = criticalRequired === 0 || criticalCount >= criticalRequired;
                        const aiGoalCount = actualGoals.filter(g => g.isAiGoal === true).length;
                        const meetsAiGoalRequirement = !aiGoalEnabled || aiGoalCount === 1;
                        const hasUnsaved = inlineNewGoal !== null || tableData.some(row => row.isEmptyRow);
                        return !meetsMinGoals || !meetsCriticalRequirement || !meetsAiGoalRequirement || hasUnsaved;
                      })()
                    ) : (
                      areGoalsSubmitted ||
                      isSubmittingGoals ||
                      isSavingGoals ||
                      (!isManager && !isSubmissionWindowActive && !(isReviewWindowActive && hasRejectedGoals)) ||
                      (() => {
                        const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                        const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;
                        const criticalCount = actualGoals.filter(g => g.critical === true).length;
                        const meetsCriticalRequirement = criticalRequired === 0 || criticalCount >= criticalRequired;
                        const aiGoalCount = actualGoals.filter(g => g.isAiGoal === true).length;
                        const meetsAiGoalRequirement = !aiGoalEnabled || aiGoalCount === 1;
                        const hasUnsaved = inlineNewGoal !== null || tableData.some(row => row.isEmptyRow);
                        return !meetsMinGoals || !meetsCriticalRequirement || !meetsAiGoalRequirement || hasUnsaved;
                      })()
                    )
                  }
                  onClick={async () => {
                    if (!onSubmitGoals || !cycleId) {
                      toast.error('Submit goals function not available');
                      return;
                    }

                    setIsSubmittingGoals(true);
                    try {
                      // Final validation
                      const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                      const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;
                      const criticalCount = actualGoals.filter(g => g.critical === true).length;
                      const meetsCriticalRequirement = criticalRequired === 0 || criticalCount >= criticalRequired;
                      const aiGoalCount = actualGoals.filter(g => g.isAiGoal === true).length;
                      const meetsAiGoalRequirement = !aiGoalEnabled || aiGoalCount === 1;

                      if (!meetsMinGoals) {
                        toast.error(`Minimum ${minGoals} goal(s) required. Currently ${actualGoals.length} goal(s) added.`);
                        setIsSubmittingGoals(false);
                        return;
                      }

                      if (!meetsCriticalRequirement) {
                        toast.error(`At least ${criticalRequired} critical goal(s) required. Currently ${criticalCount} critical goal(s).`);
                        setIsSubmittingGoals(false);
                        return;
                      }

                      if (!meetsAiGoalRequirement) {
                        toast.error(aiGoalCount === 0 ? 'Mark one goal as AI Goal before submitting.' : 'Only one goal can be marked as AI Goal.');
                        setIsSubmittingGoals(false);
                        return;
                      }

                      // Weightage validation — single rule: sum of ALL goals must equal 100.
                      // Critical is just a flag now; there is no separate critical/non-critical bucket.
                      const validatedSum = actualGoals.reduce((s, g) => s + (Number(g.weightage) || Number(g.weight) || 0), 0);
                      if (validatedSum > 100) {
                        toast.error(`Total weightage of all goals cannot exceed 100%.\nCurrent total: ${validatedSum}%.\nEdit existing goals weightage.`, { style: { whiteSpace: 'pre-line' } });
                        setIsSubmittingGoals(false);
                        return;
                      }
                      if (validatedSum !== 100) {
                        toast.error(`Total weightage of all goals must equal 100%.\nCurrent total: ${validatedSum}%.\nAdd more goals or edit existing goals weightage.`, { style: { whiteSpace: 'pre-line' } });
                        setIsSubmittingGoals(false);
                        return;
                      }

                      // Submit goals
                      await onSubmitGoals(cycleId, actualTargetUserId);
                      setHasModified(false); // Reset modification flag on successful submission
                      // Note: Success/error toast is already shown in handleSubmitGoals (MyGoals.js), so we don't show duplicate here
                    } catch (error) {
                      console.error('Error submitting goals:', error);
                      // Error toast is already shown in handleSubmitGoals, so we don't show duplicate here
                    } finally {
                      setIsSubmittingGoals(false);
                    }
                  }}
                  icon={nonCriticalWeightSum === 100 ? <ShieldCheck size={16} /> : <Target size={16} />}
                  className={`!text-[12px] !font-bold !h-10 !px-6 !rounded-xl !inline-flex !items-center !gap-2 ${(isAdminAutonomous ? (
                    isSubmittingGoals ||
                    isSavingGoals ||
                    (() => {
                      const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                      const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;
                      const criticalCount = actualGoals.filter(g => g.critical === true).length;
                      const meetsCriticalRequirement = criticalRequired === 0 || criticalCount >= criticalRequired;
                      const aiGoalCount = actualGoals.filter(g => g.isAiGoal === true).length;
                      const meetsAiGoalRequirement = !aiGoalEnabled || aiGoalCount === 1;
                      const hasUnsaved = inlineNewGoal !== null || tableData.some(row => row.isEmptyRow);
                      return !meetsMinGoals || !meetsCriticalRequirement || !meetsAiGoalRequirement || hasUnsaved;
                    })()
                  ) : (
                    areGoalsSubmitted ||
                    isSubmittingGoals ||
                    isSavingGoals ||
                    (!isSubmissionWindowActive && !isReviewWindowActive) ||
                    (() => {
                      const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                      const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;
                      const criticalCount = actualGoals.filter(g => g.critical === true).length;
                      const meetsCriticalRequirement = criticalRequired === 0 || criticalCount >= criticalRequired;
                      const aiGoalCount = actualGoals.filter(g => g.isAiGoal === true).length;
                      const meetsAiGoalRequirement = !aiGoalEnabled || aiGoalCount === 1;
                      const hasUnsaved = inlineNewGoal !== null || tableData.some(row => row.isEmptyRow);
                      return !meetsMinGoals || !meetsCriticalRequirement || !meetsAiGoalRequirement || hasUnsaved;
                    })()
                  ))
                    ? '!bg-slate-100 !text-slate-400 !cursor-not-allowed !border-slate-200 !shadow-none'
                    : nonCriticalWeightSum === 100
                      ? '!bg-blue-600 hover:!bg-blue-700 !text-white !border-blue-600 !shadow-lg !shadow-blue-500/25 animate-submit-glow'
                      : '!bg-slate-900 hover:!bg-slate-800 !text-white !border-slate-900 !shadow-md'
                    }`}
                >
                  Submit for Approval
                </Button>
              </Tooltip>
              )}
              {/* Star flow: manager verifies each goal (✓/✗) then locks in the star score. */}
              {isStarMode && ((isViewingTeamMemberGoals && allGoalsApproved) || (isAdminAutonomous && allGoalsApproved))
                && (isInEvaluationWindow || isAdminAutonomous)
                && onCalculateScore && (() => {
                  const approvedGoals = goals.filter(g => {
                    if (g.isNewGoal || g.isEmptyRow) return false;
                    const s = (g.status || '').toString().trim().toUpperCase();
                    return s === 'APPROVED' || s === 'COMPLETED';
                  });
                  const unverifiedCount = approvedGoals.filter(g => g.managerVerified == null).length;
                  const allVerified = approvedGoals.length > 0 && unverifiedCount === 0;
                  return (
                    <Tooltip title={
                      areGoalsEvaluated
                        ? 'Star score has already been calculated'
                        : allVerified
                          ? 'Lock in the star score from your ✓/✗ verdicts'
                          : `Verify all goals first — ${unverifiedCount} still unverified`
                    }>
                      <Button
                        type="primary"
                        size="large"
                        disabled={!allVerified || areGoalsEvaluated}
                        onClick={async () => {
                          try {
                            await onCalculateScore(cycleId, actualTargetUserId);
                            toast.success('Star score calculated!');
                          } catch (error) {
                            const msg = error?.response?.data?.error?.message || error?.message || 'Failed to calculate star score';
                            toast.error(msg);
                          }
                        }}
                        className={`!text-[12px] !font-semibold !h-9 !px-4 !rounded-lg ${(!allVerified || areGoalsEvaluated)
                          ? (isDark ? '!bg-white/5 !border-white/10 !text-white/40 !cursor-not-allowed' : '!bg-slate-50 !text-slate-300 !border-slate-100 !cursor-not-allowed')
                          : '!bg-[#3B6EF8] hover:!bg-[#2F58C7] !text-white !border-[#3B6EF8] !shadow-md !shadow-blue-500/20'
                          }`}
                      >
                        Calculate Star Score
                      </Button>
                    </Tooltip>
                  );
                })()}

              {/* Evaluate Goals Button — only relevant during the Evaluation window.
                  Manager viewing a team member's goals (or admin in autonomous mode). */}
              {!isStarMode && ((isViewingTeamMemberGoals && allGoalsApproved) || (isAdminAutonomous && allGoalsApproved))
                && (isInEvaluationWindow || isAdminAutonomous)
                && onSetAchievedPercent && (() => {
                  const managerSoftSkillsSubmitted = Array.isArray(softSkillsData)
                    && softSkillsData.length > 0
                    && softSkillsData.every(s => Number(s?.managerRating) > 0);
                  return (
            <Tooltip title={
              areGoalsEvaluated
                ? "Goals have already been evaluated"
                : !isAdminAutonomous && !isInEvaluationWindow
                  ? "Evaluation window is closed. Goals can only be evaluated during the evaluation window."
                  : !allGoalsApproved
                    ? (() => {
                      const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                      const meetsMinGoals = minGoals === 0 || actualGoals.length >= minGoals;
                      const allApproved = actualGoals.length > 0 && actualGoals.every(g => {
                        const status = (g.status || '').toString().trim().toUpperCase();
                        return status === 'APPROVED' || status === 'COMPLETED';
                      });

                      if (!meetsMinGoals) {
                        return `Minimum ${minGoals} goal(s) required. Currently ${actualGoals.length} goal(s) added.`;
                      }
                      if (!allApproved) {
                        return "All goals must be approved before evaluation";
                      }
                      return "All goals must be approved before evaluation";
                    })()
                    : !managerSoftSkillsSubmitted
                      ? "Submit your soft-skill ratings before evaluating goals"
                      : (Object.keys(scoreInputs).length === 0 ? "Enter scores to evaluate goals" : "Click to evaluate goals")
            }>
              <Button
                type="primary"
                size="large"
                disabled={!allGoalsApproved || areGoalsEvaluated || (!isAdminAutonomous && !isInEvaluationWindow) || Object.keys(scoreInputs).length === 0 || !managerSoftSkillsSubmitted}
                onClick={async () => {
                  // First, validate that ALL approved goals have scores entered
                  const actualGoals = goals.filter(g => !g.isNewGoal && !g.isEmptyRow);
                  const approvedGoals = actualGoals.filter(g => {
                    const status = (g.status || '').toString().trim().toUpperCase();
                    return status === 'APPROVED' || status === 'COMPLETED';
                  });

                  // Check if all approved goals have scores in scoreInputs
                  const approvedGoalIds = approvedGoals.map(g => {
                    const goalId = g._id?.toString() || g.id?.toString();
                    return goalId;
                  }).filter(Boolean);

                  const goalsWithScores = Object.keys(scoreInputs).filter(goalId => {
                    const score = scoreInputs[goalId];
                    return score && !isNaN(score) && parseFloat(score) >= 1 && parseFloat(score) <= 10;
                  });

                  // Check if all approved goals have scores
                  const allApprovedGoalsHaveScores = approvedGoalIds.length > 0 &&
                    approvedGoalIds.every(goalId => goalsWithScores.includes(goalId));

                  if (!allApprovedGoalsHaveScores) {
                    toast.error('Add score to all goals');
                    return;
                  }

                  // Evaluate all goals with score inputs
                  const promises = Object.entries(scoreInputs).map(async ([goalId, score]) => {
                    if (score && !isNaN(score) && score >= 1 && score <= 10) {
                      // Convert score to achieved %: score 1 = 10%, score 10 = 100%
                      // Handle float values: e.g., score 2.5 = 25%, score 4.56 = 45.6%
                      const achievedPercent = parseFloat(score) * 10;
                      try {
                        // Pass skipRefresh=true to prevent individual goal refreshes during batch evaluation
                        await onSetAchievedPercent(goalId, achievedPercent, true);
                      } catch (error) {
                        console.error(`Error setting score for goal ${goalId}:`, error);
                        throw error;
                      }
                    }
                  });

                  try {
                    await Promise.all(promises);

                    // Calculate final cycle score after all goals are evaluated
                    let calculationSuccess = false;
                    if (onCalculateScore && cycleId && actualTargetUserId) {
                      try {
                        await onCalculateScore(cycleId, actualTargetUserId);
                        calculationSuccess = true;
                      } catch (error) {
                        console.error('Error calculating final score:', error);
                        // Show the actual error message from backend
                        const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Failed to calculate final score';
                        toast.error(errorMessage);
                        return; // Don't show success toast if calculation failed
                      }
                    }

                    // Only proceed with refresh and success toast if calculation succeeded
                    if (calculationSuccess || !onCalculateScore) {
                      // Refresh goals once after all evaluations are complete
                      // Trigger refresh by calling onSetAchievedPercent with skipRefresh=false
                      // We'll use a dummy call that won't actually update anything but will trigger refresh
                      if (onSetAchievedPercent && Object.keys(scoreInputs).length > 0) {
                        try {
                          // Use the first goalId, but with a special flag to just refresh
                          const firstGoalId = Object.keys(scoreInputs)[0];
                          // Pass a special value to indicate refresh-only (won't call API)
                          await onSetAchievedPercent(firstGoalId, -1, false);
                        } catch (error) {
                          console.error('Error refreshing goals after evaluation:', error);
                          // Don't fail the whole operation if refresh fails
                        }
                      }

                      // Clear score inputs after successful submission
                      setScoreInputs({});

                      // Show single success toast (only once)
                      toast.success('Goals evaluated successfully!');
                    }
                  } catch (error) {
                    console.error('Error evaluating goals:', error);
                    const errorMessage = error?.response?.data?.error?.message ||
                      error?.response?.data?.message ||
                      error?.message ||
                      'Failed to evaluate some goals. Please try again.';
                    toast.error(errorMessage);
                  }
                }}
                className="!bg-blue-600 hover:!bg-blue-700 !text-white !text-[12px] !font-bold !h-10 !px-6 !rounded-xl !border-blue-600 !shadow-lg !shadow-blue-500/25 disabled:!opacity-50 disabled:!cursor-not-allowed disabled:!shadow-none"
              >
                Evaluate Goals
              </Button>
            </Tooltip>
                  );
                })()}

              {/* Review Completed — manager-only, shown during the review window on a
                  team-member's goals page. Gate: every goal APPROVED. Hidden once
                  review is already marked complete. (Soft skills moved to evaluation window.) */}
              {isViewingTeamMemberGoals && isReviewWindowActive && !reviewCompletedAt && onMarkReviewComplete && (() => {
                const canComplete = allGoalsApproved;
                const tooltipTitle = !canComplete
                  ? 'All goals must be approved before completing review'
                  : 'Mark this review as completed';
                return (
                  <Tooltip title={tooltipTitle}>
                    <Button
                      type="primary"
                      size="large"
                      loading={isMarkingReviewComplete}
                      disabled={!canComplete || isMarkingReviewComplete}
                      onClick={onMarkReviewComplete}
                      icon={<ShieldCheck size={16} />}
                      className={`!text-[12px] !font-bold !h-10 !px-6 !rounded-xl !inline-flex !items-center !gap-2 ${
                        canComplete
                          ? '!bg-emerald-600 hover:!bg-emerald-700 !text-white !border-emerald-600 !shadow-lg !shadow-emerald-500/25'
                          : '!bg-slate-100 !text-slate-400 !cursor-not-allowed !border-slate-200 !shadow-none'
                      }`}
                    >
                      Review Completed
                    </Button>
                  </Tooltip>
                );
              })()}
              </div>
            </div>
          )}
      </div>
    </motion.div>
  );
};

export default GoalsTable;
